/* eslint-disable jsx-a11y/no-autofocus */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { capitalize } from 'lodash';
import { Check, RotateCcw, X } from 'lucide-react';
import {
  CorrectReleaseRequestEditsInnerOpEnum,
  ReleaseRequestSourceEnum,
  ManageContributionsRequestEditsInnerOpEnum,
  RecordingWestburyContributionsInner,
} from '../../../../../shared_components/generated/admin';
import type {
  RecordingManualChangesResponse,
  ReleaseManualChangesResponse,
} from '../../../../../shared_components/generated/admin';
import {
  LookupLookupReleaseResponseSourceEnum,
  LookupSourceArtistLookupSourceArtistResponseSourceEnum,
} from '../../../../../shared_components/generated/admin/apis/PortalApi';
import type { Row } from '../MasterDiscographyTypes';
import { MANUAL_CHANGE_LINE_CLASS } from '../manualChangeIndicator';
import {
  ArtistChip,
  ContributionChip,
  ManualChangeChipWrap,
  ManualChangeTooltipBubble,
  ValueChip,
  MANUAL_CHANGE_TOOLTIP,
} from './manualChangeChip';
import { friendlySaveError } from './saveErrorMessage';
import { mainArtistDisplayName } from 'shared_components/utils/mainArtist';
import type { MainArtistItem } from 'shared_components/utils/mainArtist';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import WModal from 'shared_components/components/WModal';
import { useApi } from 'shared_components/context';
import { countryCodes } from 'shared_components/utils/Countries';
import { ContributionDetailModal } from './ContributionDetailModal';
import { DiscogsTrackPicker } from './DiscogsTrackPicker';
import { parseDiscogsTrackId } from '../../../utils/discogsTrackId';
import {
  ChangeReleaseModal,
  releaseSourceFormOf,
  validateReleaseYear,
  type ReleaseSelection,
} from './ChangeReleaseModal';
import {
  DROPDOWN_EMPTY_CLASS,
  DROPDOWN_META_CLASS,
  DROPDOWN_OPTION_CLASS,
  DROPDOWN_PANEL_CLASS,
  FIELD_ERROR_CLASS,
  FIELD_LABEL_CLASS,
  FIELD_OPTIONAL_CLASS,
  FIELD_SELECT_CLASS,
  SegmentedChoice,
  SegmentedToggle,
  fieldControlClass,
  fieldSelectTextClass,
} from './formStyles';
import {
  liveToString,
  findDuplicate,
  clientIsAmongArtists,
  describeVersionsError,
  isValidIsrcFormat,
  ISRC_FORMAT_ERROR,
  describeSourceIdError,
  isValidDuration,
  contributionAliasProblem,
  hasContributionAlias,
  resolveAliasIdByStageName,
  validateRecordingYear,
  validateRecordingBeforeRelease,
  type RecordingBeforeReleaseSide,
} from '../../../utils/recordingValidators';
import {
  normaliseBarcode,
  validateBarcode,
} from '../../../utils/releaseBarcode';
import {
  buildArtistEdits,
  buildContributionEdits,
  buildCreateArtistEdits,
  buildRecordingEdits,
  parseContributionEvidenceNotes,
  type RecordingValues,
} from '../../../utils/recordingOpBuilders';
import {
  buildCreateRecordingEnvelope,
  RECORDING_SOURCES,
  type RecordingSource,
} from '../../../utils/createRecordingEnvelope';
import type { CreateRecordingResponse } from 'shared_components/generated/admin';
import { useArtistLookup } from './useArtistLookup';
import {
  ArtistEditorModal,
  type ArtistEntry,
  type ArtistMember,
  type FeaturedStatus,
} from './ArtistEditorModal';

// ArtistEntry / ArtistMember / FeaturedStatus imported from ./ArtistEditorModal.

// ── Manually-edited indicators (sourced from contract § 4.5.2 / 4.5.3) ──

/** Class applied to any element (chip, pill) that carries a manual change.
 *  Adds the purple left line via a ::before pseudo-element so it stays at full
 *  height regardless of border-radius / padding / disabled state. Shared with
 *  the discography row indicator — see manualChangeIndicator.ts / global.css. */
const EDITED_LEFT_BORDER_CLASS = MANUAL_CHANGE_LINE_CLASS;

// liveToString is imported from ../../../utils/recordingValidators (shared).

// ── Shared form-field styling ──
// Field presentation lives in ./formStyles, which every editing surface reads
// from — this form, the mass-edit quick-edit modals and the bulk-edit builder —
// so a field looks the same whichever one the user came in through.
const SELECT_BASE_CLASS = FIELD_SELECT_CLASS;
const selectTextClass = fieldSelectTextClass;

/** The three-way featured status offered when adding an artist at create. */
const ADD_ARTIST_STATUS_OPTIONS: readonly {
  value: FeaturedStatus;
  label: string;
}[] = [
  { value: 'fa', label: 'Featured' },
  { value: 'ofa', label: 'Other Featured' },
  { value: 'nf', label: 'Non-Featured' },
];

/** A purple pulsating dot marking a value that has been changed locally but
 *  not yet saved. Positioned at the top-right of its relatively-positioned
 *  parent. */
const ChangedDot: React.FC = () => (
  <span
    aria-hidden
    title="Unsaved change"
    className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-surfacePurple tw-border tw-border-gray-900 tw-animate-pulse-slow tw-z-10 tw-pointer-events-none"
  />
);

/** Wraps an `<input>` / `<select>` (which can't host ::before) so the purple
 *  line renders on the wrapper instead. Renders the children unchanged when
 *  not edited/changed so untouched fields keep their original layout.
 *  - `edited`  → persisted manual change: purple left line + hover tooltip.
 *  - `changed` → unsaved local change: purple pulsating dot.
 *  Shows an instant-display tooltip on hover (defaults to "Manually Changed",
 *  but callers can pass a longer string e.g. for the live-note display). */
const ManualChangeWrap: React.FC<{
  edited: boolean;
  changed?: boolean;
  title?: string;
  children: React.ReactNode;
}> = ({ edited, changed = false, title, children }) => {
  const [hover, setHover] = useState(false);
  // Always render the same wrapper element. Switching between a bare fragment
  // and a <div> when `edited`/`changed` flips makes React remount the child
  // input mid-keystroke, which drops focus while the user is typing.
  return (
    <div
      className={edited ? MANUAL_CHANGE_LINE_CLASS : 'tw-relative'}
      onMouseEnter={edited ? () => setHover(true) : undefined}
      onMouseLeave={edited ? () => setHover(false) : undefined}
    >
      {children}
      {changed && <ChangedDot />}
      {edited && hover && (
        <ManualChangeTooltipBubble text={title ?? MANUAL_CHANGE_TOOLTIP} />
      )}
    </div>
  );
};

/** Two-option segmented switch (lime fill on the selected side). The shared
 *  implementation lives in ./formStyles; aliased here because this form calls it
 *  by its original name in several places. */
const WmcSegmentedToggle = SegmentedToggle;

/* ----------------------------------------------------------------------------
 * KNOWN_ERROR_TEST demo — SPRINT-REVIEW ONLY, REMOVE AFTER THE REVIEW.
 * With ?KNOWN_ERROR_TEST=true in the URL, a "Simulate error" button appears next
 * to "Save Changes". Clicking it cycles through synthetic errors that exercise
 * every branch of friendlySaveError() (saveErrorMessage.ts) and shows the
 * resulting message exactly as a real save failure would. To remove this feature,
 * delete this block, the `knownErrorTestEnabled`/`simulateErrorIndex` lines, and
 * the "Simulate error" button.
 * -------------------------------------------------------------------------- */
type KnownErrorScenario = { label: string; makeError: () => unknown };

/** Build a ResponseError-shaped object (what the generated API client throws on
 *  non-2xx) carrying a JSON body, so friendlySaveError parses it like a real one. */
const makeKnownErrorResponse = (body: unknown): unknown => ({
  name: 'ResponseError',
  response: new Response(JSON.stringify(body), {
    status: 400,
    headers: { 'Content-Type': 'application/json' },
  }),
});

const KNOWN_ERROR_TEST_SCENARIOS: KnownErrorScenario[] = [
  {
    label: 'Field validation',
    makeError: () =>
      makeKnownErrorResponse({
        payload: {
          isrc: ['This field may not be null.'],
          duration: ['This field is required.'],
        },
      }),
  },
  {
    label: 'Duplicate value',
    makeError: () =>
      makeKnownErrorResponse({ errors: ['DynamoDB: duplicate ISRC detected'] }),
  },
  {
    label: 'Permission denied',
    makeError: () =>
      makeKnownErrorResponse({
        errors: ['AccessDenied: not authorized to perform this operation'],
      }),
  },
  {
    label: 'Graph sync failure',
    makeError: () =>
      makeKnownErrorResponse({
        errors: ['Gremlin error: expecting value while updating graph edges'],
      }),
  },
  {
    label: 'Search index failure',
    makeError: () =>
      makeKnownErrorResponse({ errors: ['OpenSearch index update failed'] }),
  },
  {
    label: 'Duration already set',
    makeError: () =>
      makeKnownErrorResponse({
        error: 'Duration is already set by the system.',
      }),
  },
  {
    label: 'AWS validation',
    makeError: () =>
      makeKnownErrorResponse({
        errors: ['ValidationException: invalid attribute value'],
      }),
  },
  {
    label: 'Generic fallback',
    makeError: () => new Error('Unexpected failure in the save pipeline'),
  },
];

// MultiValueAddField moved to ./ChangeReleaseModal (shared).

// Confidence category styling lives with the shared chip
// (getContributionPillStyle in ./manualChangeChip).

// validateReleaseYear is imported from ./ChangeReleaseModal (shared).

/** Validate an evidence URL. Returns an error message or null if valid.
 *  The field is optional — empty/blank values pass. Any valid URL is accepted
 *  (not just http/https): a bare domain like "google.com" is allowed by retrying
 *  with a default scheme. A dotted host is still required so plain text like
 *  "foo" is rejected. */
// findDuplicate is imported from ../../../utils/recordingValidators (shared).

// Release-source enum mappers moved to ./ChangeReleaseModal (shared).

/** ISRCs reassigned from other recordings during this save. */
export interface ReassignedIsrc {
  /** The ISRC that was moved to the current recording. */
  isrc: string;
  /** Recording IDs that previously held this ISRC. */
  fromRecordingIds: string[];
}

/**
 * The little the response does not carry about the recording it just accepted.
 *
 * The response is all ids — `recording_id`, `document_id`, `release_id` — so
 * anything human-readable has to travel out of the form alongside it. This is
 * deliberately not the whole draft: its consumers are the outcome modals,
 * which quote the title back, and the Pending grid row (F8), which fills the
 * three columns that identify a recording at a glance and leaves the rest
 * blank rather than pretending a placeholder is a recording.
 */
export interface CreatedRecordingSummary {
  title: string;
  /** Featured artists, joined for display — the grid's `artist` column is a
   *  display string in exactly the same way. */
  artist: string;
  /** `''` is impossible in practice (a release is mandatory), but the type
   *  does not know that and a placeholder must not throw. */
  release: string;
  /** Release *year*, four digits, matching the grid's `year` column. */
  releaseYear: string;
}

export interface RecordingEditFormProps {
  /**
   * Which flow this form is serving.
   *
   * `edit` is the incumbent behaviour and the default, so every existing call
   * site is unaffected. `create` (WMC-515) supplies `row === null`: there is no
   * recording to hydrate from, no id to address the edit endpoints with, and no
   * persisted baseline to diff against.
   */
  mode?: 'edit' | 'create';
  row: Row | null;
  onSave: (row: Row, reassignedIsrcs?: ReassignedIsrc[]) => void;
  onCancel: () => void;
  /** When true, release and recording sections stack in one column (e.g. for side panel). */
  stacked?: boolean;
  /** When true, do not render the footer (e.g. when used in panel with external fixed footer). */
  hideFooter?: boolean;
  /** When true, show only the Release section (hide Recording & Artist section). */
  releaseOnly?: boolean;
  /** User ID (required for release lookup in Change Release modal). */
  userId?: string;
  availableFormats?: string[];
  availableTypes?: string[];
  availableContributions?: string[];
  availableArtists?: string[];
  /**
   * Create mode only: the outcome of a submission the backend accepted as
   * well-formed *and* legal.
   *
   * Only the two success-shaped statuses reach here. `invalid` stays inside the
   * form and renders against the fields (`create-invalid-inline`) — handing it
   * up would mean the modal shell deciding what to do with field errors it
   * cannot render.
   */
  onCreated?: (
    response: CreateRecordingResponse,
    summary: CreatedRecordingSummary
  ) => void;
}

export interface RecordingEditFormHandle {
  save: () => void;
  cancel: () => void;
  /** True when the recording or release section has unsaved changes. Used by
   *  the parent to confirm before switching to a different recording. */
  isDirty: () => boolean;
}

export const RecordingEditForm = forwardRef<
  RecordingEditFormHandle,
  RecordingEditFormProps
>(function RecordingEditForm(
  {
    mode = 'edit',
    row,
    onSave,
    onCancel,
    stacked = false,
    hideFooter = false,
    releaseOnly = false,
    userId,
    availableFormats = [],
    availableTypes = [],
    availableContributions = [],
    availableArtists = [],
    onCreated,
  },
  ref
) {
  const { adminApi: api } = useApi();
  const [formData, setFormData] = useState<Partial<Row>>({});
  const [labels, setLabels] = useState<string[]>([]);
  const [catNumbers, setCatNumbers] = useState<string[]>([]);
  const [versions, setVersions] = useState<string[]>([]);
  const [isrcs, setIsrcs] = useState<string[]>([]);
  // (P) line names — add/delete list like ISRCs (no rename, no positional
  // pairing with `pDates`, which is never touched from here).
  const [pNames, setPNames] = useState<string[]>([]);
  const [artists, setArtists] = useState<ArtistEntry[]>([]);
  const [contributions, setContributions] = useState<
    RecordingWestburyContributionsInner[]
  >([]);
  const [showReleaseCorrectionModal, setShowReleaseCorrectionModal] =
    useState(false);
  const [showChangeReleaseModal, setShowChangeReleaseModal] = useState(false);
  /**
   * The release chosen for a create, as a *linkage* rather than a display row.
   *
   * Create mode needs both halves of what the release modal returns: the merged
   * Row drives the fields the operator sees, and this drives the `release` arm
   * of the create envelope — specifically whether it is `mode: "link"` (the
   * release already exists) or `mode: "create"` (it has to be made first). The
   * Row cannot express that distinction, which is why 'select' mode hands it
   * back separately.
   *
   * Null in edit mode always: there the modal has already done the linking.
   */
  const [releaseSelection, setReleaseSelection] =
    useState<ReleaseSelection | null>(null);
  // Release-flow state moved to ./ChangeReleaseModal (shared).
  const [isReleaseEditable, setIsReleaseEditable] = useState(false);
  // The recording section is always editable (no "Edit Recording Details"
  // step); Save/Cancel CTAs appear only once something changes. The linked
  // release section keeps its own explicit edit step (isReleaseEditable).
  const isRecordingEditable = true;
  const [recordingSaveLoading, setRecordingSaveLoading] = useState(false);
  // Friendly error messages shown above the Save CTA when a save API call fails.
  const [recordingSaveError, setRecordingSaveError] = useState<string | null>(
    null
  );
  const [releaseSaveError, setReleaseSaveError] = useState<string | null>(null);
  // KNOWN_ERROR_TEST demo (sprint-review only — remove after the review): cycles
  // synthetic save failures through friendlySaveError to preview every message.
  const knownErrorTestEnabled =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('KNOWN_ERROR_TEST') ===
      'true';
  const [simulateErrorIndex, setSimulateErrorIndex] = useState(0);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [revertTarget, setRevertTarget] = useState<{
    type: 'section' | 'field';
    field?: string;
  } | null>(null);
  // Release-flow state moved to ./ChangeReleaseModal (shared).
  const [yearError, setYearError] = useState<string | null>(null);
  // Year of Recording shape/bounds error (same rules as the release year).
  const [yearRecordedError, setYearRecordedError] = useState<string | null>(
    null
  );
  // Recording-before-release rule: the message is derived from the current
  // form values (see recordingBeforeReleaseError below); this only remembers
  // which year field the user touched last, so the error shows under it and
  // is worded from that side.
  const [ruleErrorSide, setRuleErrorSide] =
    useState<RecordingBeforeReleaseSide>('recording');
  const [showLiveToYesModal, setShowLiveToYesModal] = useState(false);
  const [liveToYesNote, setLiveToYesNote] = useState('');
  const [isrcFormatError, setIsrcFormatError] = useState<string | null>(null);
  // Inline "add item" inputs (ISRC / Label / Catalogue Number). The input is
  // hidden until the user clicks "Add {item}"; while open, the Add button is
  // replaced with Create / Cancel CTAs. Tracked so "Save Changes" can warn
  // when an item is still being entered (not yet created/cancelled).
  const [isrcInputOpen, setIsrcInputOpen] = useState(false);
  const [isrcInputValue, setIsrcInputValue] = useState('');
  const [labelInputOpen, setLabelInputOpen] = useState(false);
  const [labelInputValue, setLabelInputValue] = useState('');
  const [catNoInputOpen, setCatNoInputOpen] = useState(false);
  const [catNoInputValue, setCatNoInputValue] = useState('');
  const [pNameInputOpen, setPNameInputOpen] = useState(false);
  const [pNameInputValue, setPNameInputValue] = useState('');
  // Version joins them in create mode only. Edit mode has never had an add
  // control here — a version arrives from source data and is renamed in place
  // — but a create starts from an empty list, so without this the field is
  // permanently stuck on "None".
  const [versionInputOpen, setVersionInputOpen] = useState(false);
  const [versionInputValue, setVersionInputValue] = useState('');
  // When a save is attempted while an inline add-item input is still open,
  // this holds the item name ("ISRC" / "label" / "catalogue number") so the
  // warning modal can tell the user to create or cancel it first.
  const [unsavedItemWarning, setUnsavedItemWarning] = useState<string | null>(
    null
  );
  const [pendingDuplicateIsrc, setPendingDuplicateIsrc] = useState<
    string | null
  >(null);
  const [pendingDuplicateMatches, setPendingDuplicateMatches] = useState<
    {
      recordingId: string;
      recordingTitle: string;
      artistName: string;
      documentId: string;
    }[]
  >([]);
  // Maps ISRC → all matching recordings it's being reassigned from
  const [confirmedDuplicateIsrcs, setConfirmedDuplicateIsrcs] = useState<
    Record<string, { recordingId: string; documentId: string }[]>
  >({});
  const [showCreateArtist, setShowCreateArtist] = useState(false);
  // Create-artist state moved to ./ArtistEditorModal (shared).
  const [pendingArtistName, setPendingArtistName] = useState<string | null>(
    null
  );
  const [pendingArtistId, setPendingArtistId] = useState<string | null>(null);
  const [pendingArtistMembers, setPendingArtistMembers] = useState<
    ArtistMember[]
  >([]);
  const [pendingArtistFeatured, setPendingArtistFeatured] =
    useState<FeaturedStatus>('fa');
  const [editingVersionIndex, setEditingVersionIndex] = useState<number | null>(
    null
  );
  const [artistDropdownOpen, setArtistDropdownOpen] = useState(false);
  // Debounced artist lookup (contract 4.4.1). Lives in ./useArtistLookup so the
  // create flow can reuse it; the names are aliased back to keep this file's
  // call sites unchanged.
  const {
    query: artistSearchQuery,
    setQuery: setArtistSearchQuery,
    results: artistLookupResults,
    loading: artistLookupLoading,
  } = useArtistLookup({
    userId,
    parentRecordingId: row?.id,
    enabled: isRecordingEditable,
  });
  const [artistsError, setArtistsError] = useState<string | null>(null);
  /** Server-side `invalid` messages, rendered verbatim above the submit button.
   *  Cleared on every new attempt so a fixed error does not linger. */
  const [createErrors, setCreateErrors] = useState<string[]>([]);
  /** A transport failure, which is not the same as `invalid` — the recording
   *  may be perfectly legal and the request simply never landed. */
  const [createSubmitError, setCreateSubmitError] = useState<string | null>(
    null
  );
  const [createSubmitting, setCreateSubmitting] = useState(false);
  /** Where the recording was found, and its id there (create only — spec
   *  §6.1 `create-source-required`). Held outside `formData` because `Row`
   *  has no such fields: the source is written once at create and the grid
   *  never hands it back to the form. */
  const [createSource, setCreateSource] = useState<RecordingSource | ''>('');
  const [createSourceId, setCreateSourceId] = useState('');
  /** Discogs only: the release whose tracklist the sourceId was picked from. */
  const [createDiscogsReleaseId, setCreateDiscogsReleaseId] = useState('');
  const [titleError, setTitleError] = useState<string | null>(null);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [labelsError, setLabelsError] = useState<string | null>(null);
  const [catNumbersError, setCatNumbersError] = useState<string | null>(null);
  const [artistActionModalIndex, setArtistActionModalIndex] = useState<
    number | null
  >(null);
  const [artistActionConfirm, setArtistActionConfirm] = useState<
    'demote' | 'remove' | null
  >(null);
  const [demotedArtists, setDemotedArtists] = useState<ArtistEntry[]>([]);
  const [showRemoveContributionModal, setShowRemoveContributionModal] =
    useState<number | null>(null);
  const [addContributionDropdownOpen, setAddContributionDropdownOpen] =
    useState(false);
  const [contributionSearchQuery, setContributionSearchQuery] = useState('');
  const [contributionCategoryOpenIndex, setContributionCategoryOpenIndex] =
    useState<number | null>(null);
  const [categoryDropdownRect, setCategoryDropdownRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const [showContributionModal, setShowContributionModal] = useState(false);
  const [contributionModalContext, setContributionModalContext] = useState<
    | { type: 'edit'; index: number }
    | { type: 'add'; contributionValue: string }
    | null
  >(null);
  const [contributionModalForm, setContributionModalForm] = useState<{
    contributionName: string;
    confidenceCategory: string;
    evidenceUrl: string;
    notes: string;
    personaId: string;
  }>({
    contributionName: '',
    confidenceCategory: '1',
    evidenceUrl: '',
    notes: '',
    personaId: '',
  });
  const [clientAliases, setClientAliases] = useState<
    { stageName: string; personaId: string }[]
  >([]);
  const [clientAliasesLoading, setClientAliasesLoading] = useState(false);
  const [releaseSaveLoading, setReleaseSaveLoading] = useState(false);
  // Manual-change overlays (contract § 4.5.2 / 4.5.3). Populated when the
  // modal opens for a row that has manual edits.
  const [editedRecording, setEditedRecording] =
    useState<RecordingManualChangesResponse | null>(null);
  const [editedRelease, setEditedRelease] =
    useState<ReleaseManualChangesResponse | null>(null);
  // Bumped after every successful save so the manual-change overlay fetch
  // re-runs without needing to close + reopen the modal.
  const [editsVersion, setEditsVersion] = useState(0);
  const [originalValues, setOriginalValues] = useState<{
    formData: Partial<Row>;
    labels: string[];
    catNumbers: string[];
    versions: string[];
    isrcs: string[];
    pNames: string[];
    artists: ArtistEntry[];
    contributions: RecordingWestburyContributionsInner[];
  } | null>(null);

  useEffect(() => {
    const validCount = artists.filter((a) => a.name.trim()).length;
    if (validCount > 0 && artistsError) setArtistsError(null);
  }, [artists, artistsError]);

  // Fetch the manual-changes overlay for the recording + release on modal
  // open (contract 4.5.2 / 4.5.3). Failures are non-fatal — the form just
  // renders without the purple-line / dot indicators.
  useEffect(() => {
    if (!row?.id || !userId) {
      setEditedRecording(null);
      setEditedRelease(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const recordingPromise = api
        .recordingManualChangesRecordingManualChangesResponse({
          userId,
          parentRecordingId: row.id,
          documentId: row.documentId,
        })
        .catch((err: unknown) => {
          console.warn('recording manual-changes lookup failed', err);
          return null;
        });
      const releasePromise = row.earliestParentReleaseId
        ? api
            .releaseManualChangesReleaseManualChangesResponse({
              userId,
              releaseId: row.earliestParentReleaseId,
              recordingId: row.id,
              documentId: row.documentId,
            })
            .catch((err: unknown) => {
              console.warn('release manual-changes lookup failed', err);
              return null;
            })
        : Promise.resolve(null);
      const [recordingResp, releaseResp] = await Promise.all([
        recordingPromise,
        releasePromise,
      ]);
      if (cancelled) return;
      setEditedRecording(recordingResp ?? null);
      setEditedRelease(releaseResp ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    api,
    userId,
    row?.id,
    row?.documentId,
    row?.earliestParentReleaseId,
    editsVersion,
  ]);

  // Helper lookups derived from the manual-changes responses ──────────────
  const editedRecordingFields = editedRecording?.fields ?? {};
  const editedReleaseFields = editedRelease?.fields ?? {};

  // True when a top-level scalar field is present (non-undefined) in either
  // response. We can't use `key in obj` because the OpenAPI generator
  // populates every optional property with `undefined` when missing, which
  // makes `'duration' in fields` true even when the BE didn't return it.
  const isFieldEdited = (key: string): boolean => {
    return (
      (editedRecordingFields as Record<string, unknown>)[key] != null ||
      (editedReleaseFields as Record<string, unknown>)[key] != null
    );
  };

  // Collect values flagged with op="add" for a multi-value field on either
  // the recording or release response. We match chips by value (per user
  // direction: "we'll be able to match it on the value").
  const addedValuesFor = (
    field:
      | 'recordingVersion'
      | 'isrcs'
      | 'pNames'
      | 'releaseLabels'
      | 'catNumbers'
  ): Set<string> => {
    const out = new Set<string>();
    const fromRec = (editedRecordingFields as any)[field];
    const fromRel = (editedReleaseFields as any)[field];
    for (const src of [fromRec, fromRel]) {
      const items: Array<{ op?: string; value?: unknown }> = src?.items ?? [];
      for (const it of items) {
        if (it.op === 'add' && typeof it.value === 'string') out.add(it.value);
      }
    }
    return out;
  };

  // Artists added (op === "add") from the recording response. We track both
  // the artist_id and the stageName so the chip render path can match either —
  // some legacy rows in `row.mainArtistsRaw` are bare strings (no id), so an
  // id-only match would miss them.
  const addedArtistIds = (): Set<string> => {
    const out = new Set<string>();
    const items: Array<{ op?: string; artistId?: string }> =
      editedRecording?.fields?.artists?.items ?? [];
    for (const it of items) {
      if (it.op === 'add' && it.artistId) out.add(it.artistId);
    }
    return out;
  };
  const addedArtistNames = (): Set<string> => {
    const out = new Set<string>();
    const items: Array<{ op?: string; stageName?: string }> =
      editedRecording?.fields?.artists?.items ?? [];
    for (const it of items) {
      if (it.op === 'add' && it.stageName) out.add(it.stageName);
    }
    return out;
  };

  // Contribution roles that were either added or edited.
  const editedContributionRoles = (): Set<string> => {
    const out = new Set<string>();
    const items: Array<{ op?: string; role?: string }> =
      editedRecording?.fields?.contributions?.items ?? [];
    for (const it of items) {
      if ((it.op === 'add' || it.op === 'edit') && it.role) out.add(it.role);
    }
    return out;
  };

  // For an edited/added contribution role, return the notes + evidence URL
  // captured in the manual-changes overlay. ADD ops carry them top-level;
  // EDIT ops carry them under `changes.{notes,source_url}.value`.
  // Returns undefined fields when the overlay didn't supply that sub-field
  // (the caller falls back to the legacy `confidencePattern` JSON).
  const getContributionOverlayForRole = (
    role: string
  ): { notes?: string; sourceUrl?: string } | null => {
    const items: Array<{
      op?: string;
      role?: string;
      notes?: string | null;
      sourceUrl?: string | null;
      changes?: {
        notes?: { value?: unknown };
        sourceUrl?: { value?: unknown };
      };
    }> = editedRecording?.fields?.contributions?.items ?? [];
    const match = items.find((it) => it.role === role);
    if (!match) return null;
    const out: { notes?: string; sourceUrl?: string } = {};
    if (match.op === 'add') {
      if (match.notes != null) out.notes = String(match.notes);
      if (match.sourceUrl != null) out.sourceUrl = String(match.sourceUrl);
    } else if (match.op === 'edit' && match.changes) {
      if (match.changes.notes && match.changes.notes.value != null)
        out.notes = String(match.changes.notes.value);
      if (match.changes.sourceUrl && match.changes.sourceUrl.value != null)
        out.sourceUrl = String(match.changes.sourceUrl.value);
    }
    return out;
  };

  // Memoise the predicate sets for the render path.
  const addedArtistIdSet = addedArtistIds();
  const addedArtistNameSet = addedArtistNames();
  const editedContributionRoleSet = editedContributionRoles();
  const addedVersionValues = addedValuesFor('recordingVersion');
  const addedIsrcValues = addedValuesFor('isrcs');
  const addedPNameValues = addedValuesFor('pNames');
  const addedLabelValues = addedValuesFor('releaseLabels');
  const addedCatNumberValues = addedValuesFor('catNumbers');

  // Create-artist lookup effects moved to ./ArtistEditorModal (shared).

  useEffect(() => {
    if (row) {
      setArtistsError(null);
      const normalizedYear = row.year
        ? row.year.length <= 4
          ? row.year
          : row.year.slice(0, 4)
        : '';
      const initialFormData = {
        release: row.release,
        year: normalizedYear,
        format: row.format,
        mediaType: row.mediaType,
        barcode: row.barcode ?? '',
        title: row.title,
        duration: row.duration,
        // `live` is a string descriptor (empty string = not live). Replaces the
        // former boolean `isLive`; `is_live` is derived from its presence on save.
        // Normalised so legacy boolean values can't crash the string code paths.
        live: liveToString(row.live),
        isFeatured: row.isFeatured,
        yearRecorded: row.yearRecorded ?? '',
        location: row.location ?? '',
      };
      setFormData(initialFormData);

      // Use original arrays from the Row to avoid lossy comma-split round-trips
      const initialLabels = row.labelsArray ?? [];
      const initialCatNumbers = row.catNumbersArray ?? [];
      const initialVersions = row.versionsArray ?? [];
      const initialIsrcs = row.isrcsArray ?? [];
      const initialPNames = row.pNames ?? [];
      const initialArtists: ArtistEntry[] =
        row.mainArtistsRaw && row.mainArtistsRaw.length > 0
          ? row.mainArtistsRaw.map((item) => {
              if (typeof item === 'string') {
                return {
                  name: item,
                  isFeatured: false,
                  featuredStatus: 'nf' as FeaturedStatus,
                };
              }
              const obj = item as MainArtistItem;
              return {
                name: obj.stageName,
                id: obj.id,
                isFeatured: false,
                featuredStatus: 'nf' as FeaturedStatus,
                type: obj.type,
              };
            })
          : row.artist
          ? row.artist
              .split(',')
              .map((name) => ({
                name: name.trim(),
                isFeatured: false,
                featuredStatus: 'nf' as FeaturedStatus,
              }))
              .filter((a) => a.name)
          : [];
      const initialContributions: RecordingWestburyContributionsInner[] =
        row.contributions && row.contributions.length > 0
          ? row.contributions.map((c) => ({
              contribution: c.contribution,
              confidenceCategory: c.confidenceCategory ?? '1',
              confidencePattern: c.confidencePattern ?? '',
            }))
          : [];

      setLabels(initialLabels);
      setCatNumbers(initialCatNumbers);
      setVersions(initialVersions);
      setIsrcs(initialIsrcs);
      setPNames(initialPNames);
      setArtists(initialArtists);
      setDemotedArtists([]);
      setContributions(initialContributions);
      setShowCreateArtist(false);
      setArtistSearchQuery('');
      setArtistDropdownOpen(false);
      setContributionCategoryOpenIndex(null);
      setCategoryDropdownRect(null);
      setShowContributionModal(false);
      setContributionModalContext(null);
      setContributionSearchQuery('');
      // Reset inline add-item inputs
      setIsrcInputOpen(false);
      setIsrcInputValue('');
      setLabelInputOpen(false);
      setLabelInputValue('');
      setCatNoInputOpen(false);
      setCatNoInputValue('');
      setPNameInputOpen(false);
      setPNameInputValue('');

      // Store original values for revert functionality
      setOriginalValues({
        formData: initialFormData,
        labels: initialLabels,
        catNumbers: initialCatNumbers,
        versions: initialVersions,
        isrcs: initialIsrcs,
        pNames: initialPNames,
        artists: initialArtists,
        contributions: initialContributions,
      });

      // Reset editing state when row changes
      setReleaseSelection(null);
      setIsReleaseEditable(false);
      setRecordingSaveError(null);
      setReleaseSaveError(null);
      setYearError(null);
      setYearRecordedError(null);
      setRuleErrorSide('recording');
      setLabelsError(null);
      setCatNumbersError(null);
      setVersionsError(null);
    } else if (mode === 'create') {
      // No row to hydrate from. Seed every field empty and — the part that
      // matters — seed `originalValues` with those *same* empties rather than
      // leaving it null. The persisted baseline for a recording that does not
      // exist yet is simply empty, which is the component-level counterpart of
      // EMPTY_BASELINE in ../../../utils/recordingOpBuilders. Every derived
      // predicate below (isFieldModified, isFieldEdited, addedValuesFor,
      // revert) then works unchanged against it, with no create branch of its
      // own: in create mode a field is "modified" exactly when the user has
      // typed something into it, which is the behaviour we want anyway.
      const initialFormData: Partial<Row> = {
        release: '',
        year: '',
        format: '',
        mediaType: '',
        barcode: '',
        title: '',
        duration: '',
        live: '',
        yearRecorded: '',
        location: '',
      };
      setArtistsError(null);
      setFormData(initialFormData);
      setLabels([]);
      setCatNumbers([]);
      setVersions([]);
      setIsrcs([]);
      setPNames([]);
      setArtists([]);
      setDemotedArtists([]);
      setContributions([]);
      setOriginalValues({
        formData: initialFormData,
        labels: [],
        catNumbers: [],
        versions: [],
        isrcs: [],
        pNames: [],
        artists: [],
        contributions: [],
      });
      setShowCreateArtist(false);
      setArtistSearchQuery('');
      setArtistDropdownOpen(false);
      setContributionCategoryOpenIndex(null);
      setCategoryDropdownRect(null);
      setShowContributionModal(false);
      setContributionModalContext(null);
      setContributionSearchQuery('');
      setIsrcInputOpen(false);
      setIsrcInputValue('');
      setLabelInputOpen(false);
      setLabelInputValue('');
      setCatNoInputOpen(false);
      setCatNoInputValue('');
      setPNameInputOpen(false);
      setPNameInputValue('');
      setReleaseSelection(null);
      setCreateSource('');
      setCreateSourceId('');
      setCreateDiscogsReleaseId('');
      setIsReleaseEditable(false);
      setRecordingSaveError(null);
      setReleaseSaveError(null);
      setYearError(null);
      setYearRecordedError(null);
      setRuleErrorSide('recording');
      setLabelsError(null);
      setCatNumbersError(null);
      setVersionsError(null);
    }
  }, [row, mode]);

  /** Fetch client aliases (personas) for the Alias dropdown in the contribution
   *  modal.
   *
   *  Create mode has no recording to scope the lookup to, so it goes to §4.6
   *  endpoint 3 — the recording-less sibling — which returns the same payload
   *  keyed on the user alone. Without it the mandatory contribution could not
   *  be filled in at all: the persona picker would be permanently empty, which
   *  reads as a broken dropdown rather than a missing endpoint. */
  const fetchClientAliases = async () => {
    if (!userId || clientAliases.length > 0) return;
    setClientAliasesLoading(true);
    try {
      const response = row?.id
        ? await api.lookupClientAliasesLookupClientAliasesResponse({
            userId,
            parentRecordingId: row.id,
          })
        : await api.lookupClientAliasesWithoutRecording({ userId });
      setClientAliases(
        response.personas.map((p) => ({
          stageName: p.stageName,
          personaId: p.personaId,
          // Not in the generated client yet — starts flowing once the backend
          // projects persona.isMainPerformingName into lookup_client_aliases
          // and the client is regenerated.
          isMain: (p as { isMain?: boolean }).isMain,
        }))
      );
    } catch {
      // Silently fail — alias field will just be empty
    } finally {
      setClientAliasesLoading(false);
    }
  };

  // Create mode needs the aliases before a contribution is opened, not on
  // demand: the checklist's "the client as one of the artists" item is judged
  // against them, and a lazily fetched list would leave that item unmet until
  // the operator happened to open the contribution modal. Sits above the
  // early return below, as every hook must -- which is why the fetch itself
  // is defined up here too.
  useEffect(() => {
    if (mode === 'create' && userId) {
      fetchClientAliases();
    }
  }, [mode, userId]);

  const saveHandlerRef = useRef<() => void>(() => {});
  // Kept in sync each render (see assignment near the save handler ref) so the
  // imperative `isDirty()` reflects the latest recording/release dirty state.
  const isDirtyRef = useRef(false);
  useImperativeHandle(
    ref,
    () => ({
      save: () => saveHandlerRef.current?.(),
      cancel: onCancel,
      isDirty: () => isDirtyRef.current,
    }),
    [onCancel]
  );

  // Edit mode still bails until the row has been hydrated; create mode has no
  // row by definition and is gated on the seeded baseline alone. Past this
  // point `row` stays nullable, so every read of it is either inside a
  // `mode === 'edit'` branch or optional-chained.
  if (!originalValues || (mode === 'edit' && !row)) return null;

  // Helper function to check if a field has been modified
  const isFieldModified = (field: string): boolean => {
    if (!originalValues) return false;

    switch (field) {
      case 'release':
        return formData.release !== originalValues.formData.release;
      case 'year':
        return formData.year !== originalValues.formData.year;
      case 'label':
        return JSON.stringify(labels) !== JSON.stringify(originalValues.labels);
      case 'catNumbers':
        return (
          JSON.stringify(catNumbers) !==
          JSON.stringify(originalValues.catNumbers)
        );
      case 'format':
        return formData.format !== originalValues.formData.format;
      case 'mediaType':
        return formData.mediaType !== originalValues.formData.mediaType;
      case 'barcode':
        return (
          normaliseBarcode(formData.barcode) !==
          normaliseBarcode(originalValues.formData.barcode)
        );
      case 'artist':
        return (
          JSON.stringify(artists) !== JSON.stringify(originalValues.artists) ||
          demotedArtists.length > 0
        );
      case 'isFeatured':
        return false; // Featured is read-only, never considered modified
      case 'title':
        return formData.title !== originalValues.formData.title;
      case 'version':
        return (
          JSON.stringify(versions) !== JSON.stringify(originalValues.versions)
        );
      case 'isrcs':
        return JSON.stringify(isrcs) !== JSON.stringify(originalValues.isrcs);
      case 'pNames':
        return JSON.stringify(pNames) !== JSON.stringify(originalValues.pNames);
      case 'yearRecorded':
        return (
          (formData.yearRecorded ?? '') !==
          (originalValues.formData.yearRecorded ?? '')
        );
      case 'location':
        return (
          (formData.location ?? '') !== (originalValues.formData.location ?? '')
        );
      case 'duration':
        return formData.duration !== originalValues.formData.duration;
      case 'live':
        return (
          liveToString(formData.live) !==
          liveToString(originalValues.formData.live)
        );
      case 'contributions':
        return (
          JSON.stringify(contributions) !==
          JSON.stringify(originalValues.contributions)
        );
      default:
        return false;
    }
  };

  // Check if any field in release section has been modified
  const isReleaseSectionModified = (): boolean => {
    return (
      isFieldModified('release') ||
      isFieldModified('year') ||
      isFieldModified('label') ||
      isFieldModified('catNumbers') ||
      isFieldModified('format') ||
      isFieldModified('mediaType') ||
      isFieldModified('barcode')
    );
  };

  // Check if any field in recording section has been modified
  const isRecordingSectionModified = (): boolean => {
    return (
      isFieldModified('artist') ||
      isFieldModified('title') ||
      isFieldModified('version') ||
      isFieldModified('isrcs') ||
      isFieldModified('duration') ||
      isFieldModified('live') ||
      isFieldModified('isFeatured') ||
      isFieldModified('contributions') ||
      isFieldModified('yearRecorded') ||
      isFieldModified('location') ||
      isFieldModified('pNames')
    );
  };

  // Recording-before-release rule, evaluated on the form's current values so
  // it tracks both the recording year and the inline release year. Blocks
  // both Save buttons while set; worded from the side touched last.
  const recordingBeforeReleaseError = validateRecordingBeforeRelease(
    formData.yearRecorded,
    formData.year,
    ruleErrorSide
  );

  // Only a changed barcode is checked, so a legacy value the pipeline stored
  // in another shape does not block saving the rest of the release.
  const barcodeError = isFieldModified('barcode')
    ? validateBarcode(formData.barcode)
    : null;

  // Release lookup/link handlers moved to ./ChangeReleaseModal (shared).

  const handleRevert = (type: 'section' | 'field', field?: string) => {
    if (type === 'section') {
      // Revert entire section
      if (field === 'release') {
        setFormData((prev) => ({
          ...prev,
          release: originalValues.formData.release,
          year: originalValues.formData.year,
          format: originalValues.formData.format,
          mediaType: originalValues.formData.mediaType,
          barcode: originalValues.formData.barcode,
        }));
        setLabels(originalValues.labels);
        setCatNumbers(originalValues.catNumbers);
      } else if (field === 'recording') {
        setFormData((prev) => ({
          ...prev,
          title: originalValues.formData.title,
          duration: originalValues.formData.duration,
          live: originalValues.formData.live,
          isFeatured: originalValues.formData.isFeatured,
          yearRecorded: originalValues.formData.yearRecorded,
          location: originalValues.formData.location,
        }));
        setYearRecordedError(null);
        setVersions(originalValues.versions);
        setIsrcs(originalValues.isrcs);
        setPNames(originalValues.pNames);
        setArtists(originalValues.artists);
        setContributions(originalValues.contributions);
        setContributionCategoryOpenIndex(null);
        setCategoryDropdownRect(null);
      }
    } else if (type === 'field' && field) {
      // Revert specific field
      switch (field) {
        case 'release':
          handleFieldChange('release', originalValues.formData.release || '');
          break;
        case 'year':
          handleFieldChange('year', originalValues.formData.year || '');
          break;
        case 'label':
          setLabels(originalValues.labels);
          break;
        case 'catNumbers':
          setCatNumbers(originalValues.catNumbers);
          break;
        case 'format':
          handleFieldChange('format', originalValues.formData.format || '');
          break;
        case 'mediaType':
          handleFieldChange(
            'mediaType',
            originalValues.formData.mediaType || ''
          );
          break;
        case 'barcode':
          handleFieldChange('barcode', originalValues.formData.barcode || '');
          break;
        case 'artist':
          setArtists(originalValues.artists);
          setDemotedArtists([]);
          break;
        case 'title':
          handleFieldChange('title', originalValues.formData.title || '');
          break;
        case 'version':
          setVersions(originalValues.versions);
          break;
        case 'isrcs':
          setIsrcs(originalValues.isrcs);
          break;
        case 'pNames':
          setPNames(originalValues.pNames);
          break;
        case 'yearRecorded':
          handleFieldChange(
            'yearRecorded',
            originalValues.formData.yearRecorded || ''
          );
          setYearRecordedError(null);
          break;
        case 'location':
          handleFieldChange('location', originalValues.formData.location || '');
          break;
        case 'duration':
          handleFieldChange('duration', originalValues.formData.duration || '');
          break;
        case 'live':
          handleFieldChange('live', originalValues.formData.live || '');
          break;
        case 'isFeatured':
          handleFieldChange(
            'isFeatured',
            originalValues.formData.isFeatured || false
          );
          break;
        case 'contributions':
          setContributions(originalValues.contributions);
          setContributionCategoryOpenIndex(null);
          setCategoryDropdownRect(null);
          break;
      }
    }
    setShowRevertModal(false);
    setRevertTarget(null);
  };

  /** Put the release back to "none chosen" — fields and linkage together. */
  const clearReleaseSelection = () => {
    setReleaseSelection(null);
    setFormData((prev) => ({
      ...prev,
      release: '',
      year: '',
      format: '',
      mediaType: '',
      barcode: '',
    }));
    setLabels([]);
    setCatNumbers([]);
    setYearError(null);
  };

  const handleReleaseCancel = () => {
    handleRevert('section', 'release');
    setReleaseSaveError(null);
    setYearError(null);
    setLabelsError(null);
    setCatNumbersError(null);
    setLabelInputOpen(false);
    setLabelInputValue('');
    setCatNoInputOpen(false);
    setCatNoInputValue('');
    onCancel();
  };

  const handleSaveReleaseDetails = async () => {
    if (!userId || !row?.id || !row.earliestParentReleaseId) return;

    if (labelInputOpen || catNoInputOpen) {
      setUnsavedItemWarning(labelInputOpen ? 'label' : 'catalogue number');
      return;
    }

    const dupLabel = findDuplicate(labels);
    if (dupLabel) {
      setLabelsError(`Duplicate label: "${dupLabel}"`);
      return;
    }
    setLabelsError(null);

    const dupCatNum = findDuplicate(catNumbers);
    if (dupCatNum) {
      setCatNumbersError(`Duplicate catalogue number: "${dupCatNum}"`);
      return;
    }
    setCatNumbersError(null);

    setReleaseSaveError(null);
    setReleaseSaveLoading(true);
    try {
      const edits: Array<{
        op: CorrectReleaseRequestEditsInnerOpEnum;
        field: string;
        value: string;
      }> = [];
      if (isFieldModified('release')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'release_title',
          value: formData.release ?? '',
        });
      }
      if (isFieldModified('year')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'date_released',
          value: formData.year ?? '',
        });
      }
      if (isFieldModified('label')) {
        const origLabels = originalValues?.labels ?? [];
        const addedLabels = labels.filter((v) => !origLabels.includes(v));
        const removedLabels = origLabels.filter((v) => !labels.includes(v));
        addedLabels.forEach((v) =>
          edits.push({
            op: CorrectReleaseRequestEditsInnerOpEnum.Add,
            field: 'release_labels',
            value: v,
          })
        );
        removedLabels.forEach((v) =>
          edits.push({
            op: CorrectReleaseRequestEditsInnerOpEnum.Delete,
            field: 'release_labels',
            value: v,
          })
        );
      }
      if (isFieldModified('catNumbers')) {
        const origCatNumbers = originalValues?.catNumbers ?? [];
        const addedCatNumbers = catNumbers.filter(
          (v) => !origCatNumbers.includes(v)
        );
        const removedCatNumbers = origCatNumbers.filter(
          (v) => !catNumbers.includes(v)
        );
        addedCatNumbers.forEach((v) =>
          edits.push({
            op: CorrectReleaseRequestEditsInnerOpEnum.Add,
            field: 'cat_numbers',
            value: v,
          })
        );
        removedCatNumbers.forEach((v) =>
          edits.push({
            op: CorrectReleaseRequestEditsInnerOpEnum.Delete,
            field: 'cat_numbers',
            value: v,
          })
        );
      }
      if (isFieldModified('format')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'format',
          value: formData.format ?? '',
        });
      }
      if (isFieldModified('mediaType')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'media_type',
          value: formData.mediaType ?? '',
        });
      }
      if (isFieldModified('barcode')) {
        // One field here; the backend writes it onto the release's upc/ean.
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'barcode',
          value: normaliseBarcode(formData.barcode),
        });
      }
      if (edits.length > 0) {
        await api.correctServiceResponse({
          userId,
          parentRecordingId: row.id,
          correctReleaseRequest: {
            releaseId: row.earliestParentReleaseId,
            edits,
            documentId: row.documentId,
          },
        });
      }
      // Propagate updated values back to the parent/grid
      const updatedRow: Row = {
        ...row,
        ...formData,
        barcode: normaliseBarcode(formData.barcode),
        label: labels.join(', '),
        catNumbers: catNumbers.join(', '),
        labelsArray: labels,
        catNumbersArray: catNumbers,
        isModified: true,
      };
      onSave(updatedRow);
      setEditsVersion((v) => v + 1);
      // Only leave edit mode on success — on failure keep the section editable
      // so the user can see the error above the Save CTA and retry.
      setIsReleaseEditable(false);
    } catch (err) {
      setReleaseSaveError(await friendlySaveError(err, 'release'));
    } finally {
      setReleaseSaveLoading(false);
    }
  };

  // ── Recording section handlers ──

  const handleRecordingCancel = () => {
    handleRevert('section', 'recording');
    setRecordingSaveError(null);
    setShowContributionModal(false);
    setContributionModalContext(null);
    setShowRemoveContributionModal(null);
    setArtistActionModalIndex(null);
    setShowCreateArtist(false);
    setPendingArtistName(null);
    setPendingArtistId(null);
    setPendingArtistFeatured('fa');
    setEditingVersionIndex(null);
    setVersionsError(null);
    setArtistDropdownOpen(false);
    setAddContributionDropdownOpen(false);
    setContributionSearchQuery('');
    setArtistSearchQuery('');
    setIsrcFormatError(null);
    setPendingDuplicateIsrc(null);
    setIsrcInputOpen(false);
    setIsrcInputValue('');
  };

  const handleSaveRecordingDetails = async () => {
    if (isrcInputOpen) {
      setUnsavedItemWarning('ISRC');
      return;
    }
    if (pNameInputOpen) {
      setUnsavedItemWarning('(P) name');
      return;
    }
    if (!formData.title?.trim()) {
      setTitleError('Recording title is required');
      return;
    }
    setTitleError(null);

    const validArtists = artists.filter((a) => a.name.trim());
    if (validArtists.length === 0) {
      setArtistsError('At least one artist is required');
      return;
    }
    setArtistsError(null);

    // Versions are rename-only — an emptied chip is a mistake, not a removal.
    const versionsProblem = describeVersionsError(versions);
    if (versionsProblem) {
      setVersionsError(versionsProblem);
      return;
    }
    setVersionsError(null);

    const yearRecordedProblem = validateRecordingYear(formData.yearRecorded);
    if (yearRecordedProblem) {
      setYearRecordedError(yearRecordedProblem);
      return;
    }
    setYearRecordedError(null);
    // The backend enforces the same rule against the stored release date;
    // checking here first gives the message under the field instead of in
    // the save error banner.
    if (recordingBeforeReleaseError) return;

    // Write-once: duration locks once the pipeline supplied one. This needs no
    // `mode === 'edit'` condition — in create mode the seeded baseline is empty
    // by construction, so `durationWasAlreadySet` is false and the field stays
    // editable, which is exactly the rule (create-writeonce-inapplicable). A
    // mode branch here would be a second place for the two flows to drift.
    const durationWasAlreadySet = !!originalValues?.formData?.duration
      ?.toString()
      .trim();
    const durationIsEditable =
      !durationWasAlreadySet || isFieldEdited('duration');
    if (
      durationIsEditable &&
      formData.duration &&
      !isValidDuration(formData.duration)
    ) {
      alert('Duration must be in HH:MM:SS format (e.g. 00:03:45)');
      return;
    }

    setRecordingSaveError(null);
    setRecordingSaveLoading(true);
    try {
      // The current form state and the baseline it is diffed against. Every op
      // below is a function of these two — see ../../../utils/recordingOpBuilders.
      // Create mode (WMC-515) will pass EMPTY_BASELINE as `original` and reuse
      // the same builders unchanged.
      const current: RecordingValues = {
        formData,
        versions,
        isrcs,
        pNames,
        artists: validArtists,
        contributions,
      };
      const original: RecordingValues = {
        formData: originalValues?.formData ?? {},
        versions: originalValues?.versions ?? [],
        isrcs: originalValues?.isrcs ?? [],
        pNames: originalValues?.pNames ?? [],
        artists: originalValues?.artists ?? [],
        contributions: originalValues?.contributions ?? [],
      };

      // ── 1. Correct recording data (contract 4.1.1) ──
      if (userId && row?.id) {
        const recordingEdits = buildRecordingEdits(
          current,
          original,
          confirmedDuplicateIsrcs
        );

        if (recordingEdits.length > 0) {
          await api.correctRecordingServiceResponse({
            userId,
            parentRecordingId: row.id,
            correctRecordingRequest: {
              edits: recordingEdits,
              documentId: row.documentId,
            },
          });
        }

        // ── 2a. Create new artists and link (contract 4.4.2) ──
        // Pending-create artists are linked via /create in a single batched call,
        // so they are excluded from the /artists payload below.
        const createEdits = buildCreateArtistEdits(
          current.artists,
          original.artists
        );
        if (createEdits.length > 0) {
          await api.createArtistAndLinkServiceResponse({
            userId,
            parentRecordingId: row.id,
            createArtistAndLinkRequest: {
              edits: createEdits,
              documentId: row.documentId,
            },
          });
        }

        // ── 2b. Change associated artists (contract 4.1.3) ──
        const artistEdits = buildArtistEdits(
          current.artists,
          original.artists,
          demotedArtists
        );
        if (artistEdits.length > 0) {
          await api.changeArtistsServiceResponse({
            userId,
            parentRecordingId: row.id,
            changeAssociatedArtistsRequest: {
              edits: artistEdits,
              documentId: row.documentId,
            },
          });
        }

        // ── 3. Manage contributions (contract 4.2.1) ──
        const contribEdits = buildContributionEdits(
          current.contributions,
          original.contributions
        );

        // Last line before the wire. The detail modal is the primary gate,
        // but the backend rewrites the contribution edge with
        // `source_id = persona_id` on an edit as well as an add — an edit
        // sent without one writes an edge with an empty source instead of
        // failing — so nothing alias-less is allowed out of here.
        const aliasless = contribEdits.find(
          (e) =>
            e.op !== ManageContributionsRequestEditsInnerOpEnum.Delete &&
            !hasContributionAlias(e.personaId)
        );
        if (aliasless) {
          // `finally` clears the loading flag.
          setRecordingSaveError(contributionAliasProblem(aliasless.role));
          return;
        }

        if (contribEdits.length > 0) {
          await api.manageContributionsServiceResponse({
            userId,
            parentRecordingId: row.id,
            manageContributionsRequest: {
              edits: contribEdits,
              documentId: row.documentId,
            },
          });
        }
      }

      // Build optimistic contributions for the table display.
      // - Unchanged contributions keep their original confidencePattern (tooltip).
      // - Edited or newly added contributions get "manual_update" as the pattern.
      // Sort by confidenceCategory descending (5→1) to match server ordering.
      const origContribMap = new Map(
        (originalValues?.contributions ?? []).map((c) => [c.contribution, c])
      );
      const optimisticContributions = contributions
        .map((c) => {
          const orig = origContribMap.get(c.contribution);
          const isNew = !orig;
          const isEdited =
            orig &&
            (orig.confidenceCategory !== c.confidenceCategory ||
              (orig as any).personaId !== (c as any).personaId);
          return {
            ...c,
            confidencePattern:
              isNew || isEdited
                ? 'manual_update'
                : orig?.confidencePattern ?? '',
          };
        })
        .sort((a, b) =>
          b.confidenceCategory.localeCompare(a.confidenceCategory)
        );

      // Create mode stops here. Everything below projects the edits back into
      // the grid row that was just patched, and in create mode there is no such
      // row — nor was a create endpoint called above, since the block that
      // issues the edit calls is gated on `row?.id`. WMC-515 F7 owns the
      // submission path and F8 the optimistic Pending row; until they land,
      // create mode saves nothing rather than crashing.
      if (!row) return;

      // Project the local artist list back into the row's `mainArtistsRaw`
      // shape so reopening the modal (without a page refresh) shows the
      // updated artist set. Artists with a known UUID are emitted as the new
      // `MainArtistItem` form; pending-create artists (where the server hasn't
      // assigned a UUID yet) fall back to the legacy string form — they'll be
      // replaced with the canonical UUID on the next refresh.
      const optimisticMainArtists: (string | MainArtistItem)[] =
        validArtists.map((a) =>
          a.id
            ? {
                id: a.id,
                stageName: a.name,
                type: a.type ?? 'persona',
              }
            : a.name
        );

      const updatedRow: Row = {
        ...row,
        ...formData,
        // Keep the boolean grid flag in sync with the live descriptor so the
        // optimistic row renders the Live column correctly before refetch.
        isLive: !!liveToString(formData.live),
        artist: validArtists.map((a) => a.name).join(', '),
        contributions: optimisticContributions,
        label: labels.join(', '),
        catNumbers: catNumbers.join(', '),
        version: versions.join(', '),
        isrcs: isrcs.join(', '),
        labelsArray: labels,
        catNumbersArray: catNumbers,
        versionsArray: versions,
        isrcsArray: isrcs,
        pNames,
        mainArtistsRaw: optimisticMainArtists,
        hasManualChanges: true,
        isModified: true,
      };
      // Build list of ISRCs reassigned from other recordings so the grid can
      // optimistically strip them from those rows.
      const reassignedIsrcs: ReassignedIsrc[] = Object.entries(
        confirmedDuplicateIsrcs
      ).map(([isrc, sources]) => ({
        isrc,
        fromRecordingIds: sources.map((s) => s.recordingId),
      }));

      onSave(
        updatedRow,
        reassignedIsrcs.length > 0 ? reassignedIsrcs : undefined
      );
      // Trigger a manual-changes overlay refetch so the just-saved edits show
      // up as purple indicators without requiring the user to close + reopen
      // the modal.
      setEditsVersion((v) => v + 1);
    } catch (err) {
      setRecordingSaveError(await friendlySaveError(err, 'recording'));
    } finally {
      setRecordingSaveLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Helper functions for managing multiple values
  const addMultipleValue = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    value: string
  ) => {
    const trimmed = value.trim();
    if (trimmed) {
      setter((prev) =>
        prev.some((v) => v.toLowerCase() === trimmed.toLowerCase())
          ? prev
          : [...prev, trimmed]
      );
    }
  };

  const removeMultipleValue = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    index: number
  ) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  const updateMultipleValue = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    index: number,
    value: string
  ) => {
    setter((prev) => prev.map((v, i) => (i === index ? value : v)));
  };

  /** Validate ISRC uniqueness via the backend OpenSearch endpoint.
   *
   *  Create mode has no recording to key the check on, so it goes to the
   *  recording-less sibling of the route (§4.6), keyed on the user alone. The
   *  answer is the same shape, so the Reassign flow and the `dupIsrcs` rider
   *  it feeds work identically on both writes. */
  const validateIsrcDuplication = async (
    isrc: string
  ): Promise<{
    isDuplicate: boolean;
    matches: {
      recordingId: string;
      recordingTitle: string;
      artistName: string;
      documentId: string;
    }[];
  }> => {
    if (!userId) {
      return { isDuplicate: false, matches: [] };
    }
    try {
      const resp = row?.id
        ? await api.validateIsrcValidateIsrcResponse({
            userId,
            parentRecordingId: row.id,
            validateIsrcRequest: { isrc },
          })
        : await api.validateIsrcWithoutRecording({
            userId,
            validateIsrcRequest: { isrc },
          });
      return {
        isDuplicate: resp.isDuplicate ?? false,
        matches: (resp.matches ?? []).map((m) => ({
          recordingId: m.recordingId ?? '',
          recordingTitle: m.recordingTitle ?? '',
          artistName: m.artistName ?? '',
          documentId: m.documentId ?? '',
        })),
      };
    } catch {
      // If the API call fails, allow the ISRC to be added (don't block on network errors)
      return { isDuplicate: false, matches: [] };
    }
  };

  const addIsrc = async (raw: string) => {
    if (!isValidIsrcFormat(raw)) {
      setIsrcFormatError(ISRC_FORMAT_ERROR);
      return;
    }
    setIsrcFormatError(null);
    const { isDuplicate, matches } = await validateIsrcDuplication(raw);
    if (isDuplicate) {
      // Leave the inline input open behind the confirmation modal; it's
      // cleared/closed once the user confirms (see the Continue handler).
      setPendingDuplicateIsrc(raw);
      setPendingDuplicateMatches(matches);
      return;
    }
    addMultipleValue(setIsrcs, raw);
    setIsrcInputValue('');
    setIsrcInputOpen(false);
  };

  /** Commit the inline Label input into the labels list, then close it. */
  const commitLabel = () => {
    const trimmed = labelInputValue.trim();
    if (!trimmed) return;
    addMultipleValue(setLabels, trimmed);
    setLabelInputValue('');
    setLabelInputOpen(false);
  };

  /** Commit the inline Catalogue Number input into the list, then close it. */
  const commitCatNo = () => {
    const trimmed = catNoInputValue.trim();
    if (!trimmed) return;
    addMultipleValue(setCatNumbers, trimmed);
    setCatNoInputValue('');
    setCatNoInputOpen(false);
  };

  /**
   * Commit the inline Version input into the versions list, then close it.
   *
   * `addMultipleValue` drops a case-insensitive duplicate silently, which on
   * the other lists is harmless but here would look like the Create button had
   * simply failed. So the duplicate is caught first and reported against the
   * field, using the same message `describeVersionsError` raises on submit.
   */
  const commitVersion = () => {
    const trimmed = versionInputValue.trim();
    if (!trimmed) return;
    if (versions.some((v) => v.toLowerCase() === trimmed.toLowerCase())) {
      setVersionsError(
        `“${trimmed}” is listed more than once. Versions must be unique.`
      );
      return;
    }
    setVersionsError(null);
    addMultipleValue(setVersions, trimmed);
    setVersionInputValue('');
    setVersionInputOpen(false);
  };

  /** Commit helpers for the Create/Link-release modal's inline inputs. */
  // commitNewLabel/commitNewCatNo moved to ./ChangeReleaseModal (shared).

  // isValidDuration is imported from ../../../utils/recordingValidators (shared).

  /**
   * The Version and Live fields, as one movable block.
   *
   * Create mode orders the form mandatory-first — Artists, Title,
   * Contributions, Release — while edit mode interleaves Version and Live
   * between Title and Contributions. Rather than keep two orderings of the same
   * markup in sync, the block is built once here and rendered in one of two
   * places. Live sits with Version deliberately: it is a descriptor *of* the
   * version, and splitting them would put an optional field between two
   * mandatory ones in create mode, which is the thing the reorder exists to
   * avoid.
   */
  const versionAndLiveFields = (
    <>
      {/* Version (Multiple) */}
      <div>
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <label
            htmlFor="modal-version-input"
            className={`tw-block tw-text-sm tw-font-medium ${
              isFieldModified('version') ? 'tw-text-white' : 'tw-text-white'
            }`}
          >
            Version
          </label>
          <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
        </div>
        {versions.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-min-h-[40px]">
            {versions.map((version, index) => {
              const isVersionEdited =
                originalValues?.versions?.[index] !== undefined &&
                originalValues.versions[index] !== version;
              return editingVersionIndex === index ? (
                <input
                  key={index}
                  type="text"
                  ref={(el) => el?.focus()}
                  value={version}
                  onChange={(e) =>
                    updateMultipleValue(setVersions, index, e.target.value)
                  }
                  onBlur={(e) => {
                    updateMultipleValue(
                      setVersions,
                      index,
                      e.target.value.trim()
                    );
                    setEditingVersionIndex(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === 'Escape') {
                      updateMultipleValue(
                        setVersions,
                        index,
                        e.currentTarget.value.trim()
                      );
                      setEditingVersionIndex(null);
                    }
                  }}
                  className="tw-bg-formInputBackground tw-border tw-border-westburyBlue tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-white tw-outline-none tw-min-w-[60px] tw-max-w-[160px]"
                />
              ) : (
                <ManualChangeChipWrap
                  key={index}
                  edited={addedVersionValues.has(version)}
                >
                  <div
                    role={isRecordingEditable ? 'button' : undefined}
                    tabIndex={isRecordingEditable ? 0 : undefined}
                    className={`tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-formInputBackground tw-text-white tw-border-2 tw-border-transparent ${
                      isRecordingEditable
                        ? 'tw-cursor-pointer hover:tw-border-white/50'
                        : 'tw-opacity-60'
                    }`}
                    title={
                      addedVersionValues.has(version)
                        ? undefined
                        : isRecordingEditable
                        ? 'Click to edit'
                        : undefined
                    }
                    onClick={() => {
                      if (isRecordingEditable) setEditingVersionIndex(index);
                    }}
                    onKeyDown={(e) => {
                      if (
                        isRecordingEditable &&
                        (e.key === 'Enter' || e.key === ' ')
                      ) {
                        e.preventDefault();
                        setEditingVersionIndex(index);
                      }
                    }}
                  >
                    {isVersionEdited && (
                      <span className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-surfacePurple tw-border tw-border-gray-900 tw-animate-pulse-slow" />
                    )}
                    <span>{version}</span>
                    {/* Versions is the only multi-value list in the
                                  form without an explicit remove control —
                                  edit mode drops a version by blanking it in
                                  place, a convention that only exists because
                                  there was no X. That is unlearnable for a
                                  first-time create, so create mode gets the
                                  same X as the ISRC, label, cat-no and (P)-name
                                  chips, wired to the same
                                  `removeMultipleValue`.

                                  Not added to edit mode here: that changes an
                                  established interaction for every existing
                                  recording, which is its own decision. */}
                    {mode === 'create' && isRecordingEditable && (
                      <button
                        type="button"
                        // The chip itself is click-to-edit, so
                        // without this the X would open the rename
                        // input on the row it just deleted.
                        onClick={(e) => {
                          e.stopPropagation();
                          removeMultipleValue(setVersions, index);
                          if (versionsError) setVersionsError(null);
                        }}
                        className="tw-ml-1 tw-text-gray-400 hover:tw-text-white tw-flex tw-items-center"
                        title="Remove"
                        aria-label={`Remove ${version}`}
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </ManualChangeChipWrap>
              );
            })}
          </div>
        )}
        {versions.length === 0 && (
          <p className="tw-text-textSeal tw-text-xs tw-mb-2">None</p>
        )}
        {/* Create mode only, matching the X on the chips above. Edit mode
            renames a version in place and has never offered an add, so leaving
            it alone keeps an established interaction unchanged — the same call
            the remove control made. */}
        {mode === 'create' && isRecordingEditable && (
          <div className="tw-flex tw-flex-col tw-gap-1.5">
            {versionInputOpen ? (
              <>
                <input
                  id="modal-version-input"
                  type="text"
                  autoFocus
                  placeholder="Enter version"
                  value={versionInputValue}
                  onChange={(e) => {
                    setVersionInputValue(e.target.value);
                    if (versionsError) setVersionsError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && versionInputValue.trim()) {
                      e.preventDefault();
                      commitVersion();
                    } else if (e.key === 'Escape') {
                      setVersionInputValue('');
                      setVersionInputOpen(false);
                      setVersionsError(null);
                    }
                  }}
                  className={fieldControlClass({ className: 'tw-flex-1' })}
                />
                <div className="tw-flex tw-justify-end tw-gap-2">
                  <WButton
                    variant="secondary"
                    label="Cancel"
                    onClick={() => {
                      setVersionInputValue('');
                      setVersionInputOpen(false);
                      setVersionsError(null);
                    }}
                  />
                  <WButton
                    variant="primary"
                    label="Create"
                    disabled={!versionInputValue.trim()}
                    onClick={commitVersion}
                  />
                </div>
              </>
            ) : (
              <div className="tw-flex tw-justify-end">
                <button
                  type="button"
                  className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                  onClick={() => {
                    setVersionInputValue('');
                    setVersionsError(null);
                    setVersionInputOpen(true);
                  }}
                >
                  <i className="wi wi-20px wi-plus tw-mr-1" />
                  <span className="tw-underline">Add version</span>
                </button>
              </div>
            )}
          </div>
        )}
        {versionsError && (
          <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>{versionsError}</p>
        )}
      </div>

      {/* Live — standalone string descriptor shown beneath Version.
                    It is stored only in the `live` field (never mirrored into
                    recordingVersion). Once set it can be updated (re-running
                    the live op) but never cleared back to empty. */}
      <div>
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <label
            htmlFor="modal-live"
            className="tw-block tw-text-sm tw-font-medium tw-text-white"
          >
            Live
          </label>
          {mode === 'create' && (
            <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
          )}
        </div>
        {(() => {
          const liveValue = liveToString(formData.live);
          const liveField = (editedRecordingFields as any).live as
            | { note?: string }
            | undefined;
          const note = liveField?.note;
          // Only a *persisted* manual change drives the "manually
          // edited" left ribbon. An unsaved switch (e.g. No → Live)
          // is represented solely by the pulsing "changed" dot
          // until the user saves — at which point the overlay
          // refetch flips isFieldEdited('live') on. Mirrors how the
          // Title field separates edited (ribbon) from changed (dot).
          const liveEdited = isFieldEdited('live');
          // The live descriptor is locked once it comes from source
          // data: it may only be set when empty, or re-edited after a
          // manual change — either persisted (`liveEdited`) or made
          // this session but not yet saved (`isFieldModified`). A
          // source-provided value that was never manually changed is
          // read-only. Enforced on the backend too (correct 4.1.1).
          // Needs no `mode === 'edit'` condition either: in create
          // mode `liveValue` starts empty (first clause), and once
          // the user sets it, `isFieldModified` holds against the
          // empty baseline (third clause). The gate never locks a
          // value the user typed themselves.
          const isLiveEditable =
            isRecordingEditable &&
            (!liveValue || liveEdited || isFieldModified('live'));
          const tooltip = note
            ? `${MANUAL_CHANGE_TOOLTIP} — ${note}`
            : MANUAL_CHANGE_TOOLTIP;
          if (!liveValue) {
            return (
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-min-h-[40px]">
                <p className="tw-text-textSeal tw-text-xs">
                  {mode === 'create' ? 'None' : 'No'}
                </p>
                {/* Create has nothing to "change": there is no persisted
                    value yet, so the CTA adds rather than changes, in the same
                    link style as "Add version" beside it. */}
                {isLiveEditable && mode === 'create' && (
                  <button
                    type="button"
                    className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                    onClick={() => {
                      setLiveToYesNote('');
                      setShowLiveToYesModal(true);
                    }}
                  >
                    <i className="wi wi-20px wi-plus tw-mr-1" />
                    <span className="tw-underline">Add Live</span>
                  </button>
                )}
                {isLiveEditable && mode === 'edit' && (
                  <WButton
                    variant="secondary"
                    label="Change to Live"
                    onClick={() => {
                      setLiveToYesNote('');
                      setShowLiveToYesModal(true);
                    }}
                  />
                )}
              </div>
            );
          }
          return (
            <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-min-h-[40px]">
              <ManualChangeChipWrap edited={liveEdited}>
                <div
                  role={isLiveEditable ? 'button' : undefined}
                  tabIndex={isLiveEditable ? 0 : undefined}
                  title={
                    isLiveEditable
                      ? 'Click to update the live version'
                      : tooltip
                  }
                  className={`tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-formInputBackground tw-text-white tw-border-2 tw-border-transparent ${
                    isLiveEditable
                      ? 'tw-cursor-pointer hover:tw-border-white/50'
                      : 'tw-opacity-60'
                  }`}
                  onClick={() => {
                    if (isLiveEditable) {
                      setLiveToYesNote(liveValue);
                      setShowLiveToYesModal(true);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (
                      isLiveEditable &&
                      (e.key === 'Enter' || e.key === ' ')
                    ) {
                      e.preventDefault();
                      setLiveToYesNote(liveValue);
                      setShowLiveToYesModal(true);
                    }
                  }}
                >
                  {isFieldModified('live') && (
                    <span className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-surfacePurple tw-border tw-border-gray-900 tw-animate-pulse-slow" />
                  )}
                  <span>{liveValue}</span>
                  {/* "Set once, never cleared" is a rule about *stored* data:
                      clearing a persisted live descriptor would need an op
                      that unsets it, which the backend does not accept. In
                      create mode there is nothing stored yet — the value only
                      exists in this form — so the rule has nothing to protect,
                      and enforcing it just means one mis-click makes the
                      recording permanently live. */}
                  {mode === 'create' && isRecordingEditable && (
                    <button
                      type="button"
                      // The chip itself opens the update modal, so without
                      // this the X would reopen it on the value it cleared.
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFieldChange('live', '');
                      }}
                      className="tw-ml-1 tw-text-gray-400 hover:tw-text-white tw-flex tw-items-center"
                      title="Remove"
                      aria-label={`Remove ${liveValue}`}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </ManualChangeChipWrap>
            </div>
          );
        })()}
      </div>
    </>
  );

  // Discogs derives the id from the picked track; `other` has none. Either
  // way the operator does not type it.
  const sourceIdNotTyped = createSource === 'other' || createSource === 'discogs';
  // Malformed, as opposed to missing (which the checklist reports).
  const createSourceIdError = describeSourceIdError(createSource, createSourceId);

  /**
   * Discogs: the release the recording was picked from, read off the minted
   * id, and the only release it may be put on. `null` until a track is picked
   * (or for any other source), which is why Add Release waits for the pick:
   * before it there is nothing to pin the release modal to.
   */
  const pinnedDiscogsReleaseId =
    createSource === 'discogs'
      ? parseDiscogsTrackId(createSourceId)?.releaseId ?? null
      : null;
  const releaseWaitsForTrackPick =
    createSource === 'discogs' && !pinnedDiscogsReleaseId;
  /**
   * A chosen release that is not the pinned one. The modal cannot produce one
   * while pinned, so this only arises when the pick moves *after* the release
   * was chosen — a different release id fetched and a track picked from it.
   * Reported rather than silently cleared: the operator chose that release,
   * and a field that empties itself reads as a bug.
   */
  const releasePinError: string | null =
    mode === 'create' &&
    pinnedDiscogsReleaseId &&
    releaseSelection &&
    (releaseSelection.source !== 'discogs' ||
      releaseSelection.sourceId !== pinnedDiscogsReleaseId)
      ? `A Discogs recording can only be put on the release it was picked from (${pinnedDiscogsReleaseId}). Clear this release, or pick the track from it.`
      : null;

  // The client's alias artists, by id. An artist picked from lookup carries
  // the graph ids the alias list carries too — its own and its members' — so
  // `clientIsAmongArtists` is a plain membership test, the same one the
  // backend makes against `get_client_aliases`.
  const clientAliasIds = new Set(clientAliases.map((a) => a.personaId));

  /**
   * The things a recording cannot be created without (contract 4.1.4.4), each
   * with the name the checklist shows the user.
   *
   * Named rather than counted. "3 of 4 requirements met" tells an operator they
   * are wrong without telling them where to look; on a form this long that is a
   * scavenger hunt. Naming each one costs nothing and makes the disabled submit
   * self-explaining.
   *
   * Deliberately the *same* set the backend enforces, and no more. The two
   * lists will drift if anyone adds a rule to only one of them, so this is the
   * place to add the matching entry — not a reason to loosen the backend.
   */
  const createRequirements: { label: string; met: boolean }[] = [
    { label: 'A source', met: createSource !== '' },
    // Every source but `other` is a catalogue, and an entry in it has an id.
    {
      label: 'A source recording id',
      met: createSource === 'other' || createSourceId.trim() !== '',
    },
    { label: 'At least one artist', met: artists.length > 0 },
    // The discography is the client's, so one of the artists has to be them:
    // under one of their aliases, or as a member of a listed band.
    {
      label: 'The client as one of the artists, or a member of one',
      met: clientIsAmongArtists(artists, clientAliasIds),
    },
    { label: 'A recording title', met: (formData.title ?? '').trim() !== '' },
    { label: 'At least one contribution', met: contributions.length > 0 },
    // A release is deliberately not here: a recording may be created without
    // one and linked later (`create-release-optional`).
  ];
  const unmetCreateRequirements = createRequirements.filter((r) => !r.met);

  /**
   * Formatting problems, which are a different thing from missing fields.
   *
   * A missing field is listed in the checklist; a malformed one is already
   * reported inline against the field itself, so repeating it in the checklist
   * would say the same thing twice in two places. Both block submission.
   */
  const createFormatErrors =
    !!titleError ||
    !!createSourceIdError ||
    !!releasePinError ||
    !!yearRecordedError ||
    !!recordingBeforeReleaseError ||
    !!versionsError ||
    !!isrcFormatError ||
    (!!formData.duration && !isValidDuration(formData.duration));

  const canSubmitCreate =
    unmetCreateRequirements.length === 0 && !createFormatErrors;

  /**
   * Submit the create envelope and branch on `status` (spec §6.2).
   *
   * Three outcomes, and the branch reads **only** `response.status`
   * (`create-branch-on-status`). Nothing here inspects `errors[]` to decide
   * what to do — the strings are rendered, never parsed. That matters more
   * than it looks: `create_validation.py` states outright that nothing may
   * parse its messages, and their taxonomy is still an open item on the BE-DS
   * contract, so any control flow keyed on their wording would break the first
   * time someone reworded one.
   *
   * `invalid` is rendered inline and the modal stays open, because it is a
   * fixable state and a modal would just have to be dismissed before the
   * operator could act on it. The two success-shaped statuses are handed up to
   * the modal shell, which owns the outcome modals.
   */
  const handleCreateSubmit = async () => {
    if (!userId || !canSubmitCreate || createSubmitting || !createSource) {
      return;
    }

    setCreateErrors([]);
    setCreateSubmitError(null);
    setCreateSubmitting(true);
    try {
      const envelope = buildCreateRecordingEnvelope({
        values: {
          formData,
          versions,
          isrcs,
          pNames,
          // Same filter the edit save applies: a half-typed artist row is not
          // an artist.
          artists: artists.filter((a) => a.name.trim()),
          contributions,
        },
        source: createSource,
        sourceId: createSourceId,
        release: releaseSelection,
        confirmedDuplicates: confirmedDuplicateIsrcs,
      });

      const response = await api.createRecordingCreateRecordingResponse({
        userId,
        createRecordingEnvelope: envelope,
      });

      if (response.status === 'invalid') {
        // Server-side rules the checklist cannot see — the year rule against
        // the inbound release date, formats, duplicate versions. Rendered as a
        // block above the submit button rather than attributed to fields:
        // attribution would mean matching on message text, which is exactly
        // what `no-error-string-sniffing` and the backend's own docstring
        // forbid. When 4.1.4 grows structured error codes, this is the one
        // place that changes.
        setCreateErrors(response.errors ?? []);
        return;
      }
      if (response.status === 'failed') {
        // Legal, but it could not be stored, and the backend reverted what it
        // wrote. The draft stays, so the operator can retry as-is.
        setCreateSubmitError(
          'The recording could not be created, so nothing was saved. Please try again.'
        );
        return;
      }

      onCreated?.(response, {
        title: (formData.title ?? '').trim(),
        artist: artists
          .map((a) => a.name.trim())
          .filter(Boolean)
          .join(', '),
        release: releaseSelection?.releaseTitle ?? '',
        releaseYear: (releaseSelection?.dateReleased ?? '').slice(0, 4),
      });
    } catch (err) {
      setCreateSubmitError(await friendlySaveError(err, 'recording'));
    } finally {
      setCreateSubmitting(false);
    }
  };

  saveHandlerRef.current = handleSaveRecordingDetails;
  isDirtyRef.current =
    isRecordingSectionModified() ||
    isReleaseSectionModified() ||
    createSource !== '' ||
    createSourceId.trim() !== '' ||
    createDiscogsReleaseId.trim() !== '';

  return (
    <>
      <div
        className={
          stacked
            ? 'tw-flex tw-flex-col tw-gap-0 tw-mt-2 tw-pb-2'
            : // Edit mode has no footer below this, so capping the columns
              // against the viewport is the whole story. Create mode does: a
              // fixed cap here plus a footer overflows the modal's own 90vh,
              // and since the modal clips (`tw-overflow-hidden`) the footer —
              // the Create button — is simply unreachable. So the columns take
              // whatever the flex parent has left instead, and `min-h-0` lets
              // them actually shrink to make room.
              `tw-grid tw-grid-cols-[1fr_auto_1fr] tw-gap-0 tw-mt-2 tw-pb-2 tw-overflow-y-auto ${
                mode === 'create'
                  ? 'tw-flex-1 tw-min-h-0'
                  : 'tw-max-h-[calc(90vh-100px)]'
              }`
        }
      >
        {!releaseOnly && (
          <>
            {/* Recording section (includes Contributions, Artists, and Recording fields) */}
            <div
              className={
                stacked
                  ? 'tw-flex tw-flex-col tw-shrink-0'
                  : 'tw-flex tw-flex-col tw-overflow-y-auto'
              }
            >
              <div className="tw-px-3 tw-pt-2 tw-pb-2 tw-flex tw-flex-col tw-shrink-0">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-pb-1.5 tw-border-b tw-border-formInputBackground tw-flex-shrink-0">
                  <div className="tw-text-white tw-font-semibold">
                    Recording
                  </div>
                </div>
                <div className="tw-space-y-4">
                  {/* ── Source (create only) ──
                      First because the flow is source-first: naming where the
                      recording was found is what lets DS recognise one it
                      already has. Edit mode never shows a source — a row's
                      source is read-only and lives on the grid. */}
                  {mode === 'create' && (
                    <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                      <div className="tw-flex-1 tw-min-w-0">
                        <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                          <label
                            htmlFor="modal-source"
                            className="tw-block tw-text-sm tw-font-medium tw-text-white"
                          >
                            Source
                          </label>
                        </div>
                        <select
                          id="modal-source"
                          value={createSource}
                          onChange={(e) => {
                            const next = e.target.value as RecordingSource | '';
                            setCreateSource(next);
                            // An id is meaningless at a different source, and
                            // an `other` recording has none at all. So is a
                            // chosen release: it has to share the recording's
                            // source, and the Discogs one is pinned to a pick
                            // that no longer exists.
                            setCreateSourceId('');
                            setCreateDiscogsReleaseId('');
                            setReleaseSelection(null);
                          }}
                          className={fieldControlClass({
                            kind: 'select',
                            hasValue: !!createSource,
                          })}
                        >
                          <option value="">Select source</option>
                          {RECORDING_SOURCES.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {createSource === 'discogs' && (
                        <DiscogsTrackPicker
                          releaseId={createDiscogsReleaseId}
                          onReleaseIdChange={setCreateDiscogsReleaseId}
                          sourceId={createSourceId}
                          onSourceIdChange={setCreateSourceId}
                        />
                      )}
                      <div className="tw-flex-1 tw-min-w-0">
                        <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                          <label
                            htmlFor="modal-source-id"
                            className="tw-block tw-text-sm tw-font-medium tw-text-white"
                          >
                            Source Recording ID
                          </label>
                        </div>
                        <input
                          id="modal-source-id"
                          type="text"
                          value={createSourceId}
                          onChange={(e) => setCreateSourceId(e.target.value)}
                          disabled={sourceIdNotTyped}
                          className={fieldControlClass({
                            locked: sourceIdNotTyped,
                          })}
                          placeholder={
                            createSource === 'discogs'
                              ? 'Derived from the track picked above'
                              : createSource === 'other'
                              ? 'An Other recording has no source id'
                              : "The recording's id at the selected source"
                          }
                        />
                        {createSourceIdError && (
                          <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
                            {createSourceIdError}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  {/* ── Artists subsection ── */}
                  <div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
                      <label
                        htmlFor="modal-artist-select"
                        className={`tw-text-sm tw-font-medium ${
                          isFieldModified('artist')
                            ? 'tw-text-white'
                            : 'tw-text-white'
                        }`}
                      >
                        Artists
                      </label>
                      <div className="tw-flex tw-items-center tw-gap-3">
                        {isRecordingEditable && (
                          <button
                            type="button"
                            onClick={() => {
                              setShowCreateArtist(true);
                            }}
                            className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                          >
                            <i className="wi wi-20px wi-plus tw-mr-1" />
                            <span className="tw-underline">Create artist</span>
                          </button>
                        )}
                      </div>
                    </div>
                    {artistsError && (
                      <p className="tw-text-westburyRed tw-text-xs tw-mb-1">
                        {artistsError}
                      </p>
                    )}
                    {artists.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-1.5">
                        {artists.map((artist, index) => {
                          const origArtist = originalValues?.artists?.find(
                            (oa) => oa.name === artist.name
                          );
                          const isArtistAdded = !origArtist;
                          // Left-border indicator when this artist is in the
                          // manual-changes overlay (contract 4.5.2 artists.items)
                          // OR when the user just added them locally. We match
                          // on artist_id when available, falling back to
                          // stageName so legacy rows (where mainArtistsRaw is a
                          // plain string array) still light up correctly.
                          const showEditedBorder =
                            isArtistAdded ||
                            (!!artist.id && addedArtistIdSet.has(artist.id)) ||
                            addedArtistNameSet.has(artist.name);
                          // "Never remove the last one" guards a *stored*
                          // recording, which must always have an artist and
                          // has no remove-then-add transaction to get there.
                          // A create has neither problem: nothing is stored,
                          // and the Create button is already gated on "At
                          // least one artist", so emptying the list blocks
                          // submission rather than corrupting anything.
                          const removable =
                            isRecordingEditable &&
                            (mode === 'create' || artists.length > 1);
                          return (
                            <ArtistChip
                              key={index}
                              name={artist.name}
                              edited={showEditedBorder}
                              removeTitle={
                                artist.pendingCreate || mode === 'create'
                                  ? 'Remove artist'
                                  : 'Demote or remove artist'
                              }
                              onRemove={
                                removable
                                  ? () => {
                                      // Demotion is a change to an existing
                                      // linkage; in create mode there is no
                                      // linkage yet, so every removal here is
                                      // the plain local one.
                                      if (
                                        artist.pendingCreate ||
                                        mode === 'create'
                                      ) {
                                        // Not yet created/linked — just remove locally
                                        setArtists((prev) =>
                                          prev.filter((_, i) => i !== index)
                                        );
                                      } else {
                                        setArtistActionModalIndex(index);
                                        setArtistActionConfirm(null);
                                      }
                                    }
                                  : undefined
                              }
                            />
                          );
                        })}
                      </div>
                    )}
                    <div className="tw-relative">
                      <input
                        id="modal-artist-select"
                        type="text"
                        value={artistSearchQuery}
                        onChange={(e) => {
                          setArtistSearchQuery(e.target.value);
                          setArtistDropdownOpen(true);
                        }}
                        onFocus={() => {
                          if (isRecordingEditable) setArtistDropdownOpen(true);
                        }}
                        onBlur={() => {
                          setTimeout(() => setArtistDropdownOpen(false), 200);
                        }}
                        placeholder="Search or select artist…"
                        readOnly={!isRecordingEditable}
                        disabled={!isRecordingEditable}
                        className={fieldControlClass()}
                      />
                      {artistDropdownOpen && (
                        <div className={DROPDOWN_PANEL_CLASS}>
                          {!artistSearchQuery.trim() ? (
                            <div className={DROPDOWN_EMPTY_CLASS}>
                              Start typing to search for artists…
                            </div>
                          ) : artistLookupLoading ? (
                            <div className={DROPDOWN_EMPTY_CLASS}>
                              Searching...
                            </div>
                          ) : (
                            (() => {
                              const notAdded = artistLookupResults.filter(
                                (a) =>
                                  !artists.some(
                                    (e) =>
                                      e.name === a.stageName || e.id === a.id
                                  )
                              );
                              if (notAdded.length === 0) {
                                return (
                                  <div className={DROPDOWN_EMPTY_CLASS}>
                                    {artistLookupResults.length === 0
                                      ? 'No matching artists'
                                      : 'All matching artists already added'}
                                  </div>
                                );
                              }
                              return notAdded.map((artist) => (
                                <button
                                  key={artist.id}
                                  type="button"
                                  className={DROPDOWN_OPTION_CLASS}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setPendingArtistName(artist.stageName);
                                    setPendingArtistId(artist.id);
                                    setPendingArtistMembers(
                                      artist.members ?? []
                                    );
                                    setPendingArtistFeatured('fa');
                                    setArtistSearchQuery('');
                                    setArtistDropdownOpen(false);
                                  }}
                                >
                                  <span>{artist.stageName}</span>
                                  <span className={DROPDOWN_META_CLASS}>
                                    {artist.id}
                                  </span>
                                </button>
                              ));
                            })()
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Recording Title */}
                  <div>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                      <label
                        htmlFor="modal-title"
                        className={`tw-block tw-text-sm tw-font-medium ${
                          isFieldModified('title')
                            ? 'tw-text-white'
                            : 'tw-text-white'
                        }`}
                      >
                        Recording Title
                      </label>
                    </div>
                    <ManualChangeWrap
                      edited={isFieldEdited('recordingTitle')}
                      changed={isFieldModified('title')}
                    >
                      <input
                        id="modal-title"
                        type="text"
                        value={formData.title || ''}
                        onChange={(e) => {
                          handleFieldChange('title', e.target.value);
                          if (titleError && e.target.value.trim())
                            setTitleError(null);
                        }}
                        readOnly={!isRecordingEditable}
                        disabled={!isRecordingEditable}
                        className={fieldControlClass({ error: !!titleError })}
                      />
                    </ManualChangeWrap>
                    {titleError && (
                      <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
                        {titleError}
                      </p>
                    )}
                  </div>

                  {/* Version and Live render here in edit mode; in create
                      mode they slide below Contributions so the four
                      mandatory fields come first. See
                      `versionAndLiveFields` for why this is one block
                      rendered in one of two places rather than two
                      orderings of the same markup. */}
                  {mode === 'edit' && versionAndLiveFields}

                  {/* ── Contributions subsection ── */}
                  <div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
                      <label
                        htmlFor="modal-contribution-search"
                        className={`tw-text-sm tw-font-medium ${
                          isFieldModified('contributions')
                            ? 'tw-text-white'
                            : 'tw-text-white'
                        }`}
                      >
                        Contributions
                      </label>
                    </div>
                    {contributions.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-1.5">
                        {[...contributions]
                          .map((cont, originalIndex) => ({
                            cont,
                            originalIndex,
                          }))
                          .sort(
                            (a, b) =>
                              parseInt(a.cont.confidenceCategory ?? '1', 10) -
                              parseInt(b.cont.confidenceCategory ?? '1', 10)
                          )
                          .map(({ cont, originalIndex }) => {
                            const origContrib =
                              originalValues?.contributions?.find(
                                (oc) => oc.contribution === cont.contribution
                              );
                            const isAdded = !origContrib;
                            const isEdited =
                              !!origContrib &&
                              (origContrib.confidenceCategory !==
                                cont.confidenceCategory ||
                                origContrib.confidencePattern !==
                                  cont.confidencePattern);
                            const openDetail = () => {
                              const c = contributions[originalIndex];
                              const parsed = parseContributionEvidenceNotes(
                                c.confidencePattern ?? ''
                              );
                              const overlay = getContributionOverlayForRole(
                                c.contribution ?? ''
                              );
                              setContributionModalForm({
                                contributionName: c.contribution ?? '',
                                confidenceCategory: c.confidenceCategory ?? '1',
                                evidenceUrl:
                                  overlay?.sourceUrl ?? parsed.evidenceUrl,
                                notes: overlay?.notes ?? parsed.notes,
                                // The read path never carries the
                                // contribution's alias (the serializer returns
                                // only contribution / pattern / category), so
                                // this is effectively always blank. Seed it
                                // with the persona credited on the recording —
                                // a default the user confirms, since the modal
                                // still blocks the save on a blank alias.
                                personaId:
                                  (c as any).personaId ||
                                  resolveAliasIdByStageName(
                                    row?.stageName,
                                    clientAliases
                                  ),
                              });
                              setContributionModalContext({
                                type: 'edit',
                                index: originalIndex,
                              });
                              fetchClientAliases();
                              setShowContributionModal(true);
                            };
                            return (
                              <ContributionChip
                                key={originalIndex}
                                role={cont.contribution ?? ''}
                                confidenceCategory={cont.confidenceCategory}
                                edited={editedContributionRoleSet.has(
                                  cont.contribution ?? ''
                                )}
                                changed={isAdded || isEdited}
                                changedTitle={
                                  isAdded
                                    ? 'Newly added contribution'
                                    : 'Edited contribution'
                                }
                                onSelect={
                                  isRecordingEditable ? openDetail : undefined
                                }
                                onRemove={
                                  // As with artists: the "keep at least one"
                                  // floor protects a stored recording. A
                                  // create is gated on "At least one
                                  // contribution" before it can be submitted,
                                  // so emptying the list here is recoverable.
                                  isRecordingEditable &&
                                  (mode === 'create' ||
                                    contributions.length > 1)
                                    ? () =>
                                        setShowRemoveContributionModal(
                                          originalIndex
                                        )
                                    : undefined
                                }
                              />
                            );
                          })}
                      </div>
                    )}
                    <div className="tw-relative">
                      <input
                        id="modal-contribution-search"
                        type="text"
                        value={contributionSearchQuery}
                        onChange={(e) => {
                          setContributionSearchQuery(e.target.value);
                          setAddContributionDropdownOpen(true);
                        }}
                        onFocus={() => {
                          if (isRecordingEditable)
                            setAddContributionDropdownOpen(true);
                        }}
                        onBlur={() => {
                          setTimeout(
                            () => setAddContributionDropdownOpen(false),
                            200
                          );
                        }}
                        placeholder="Search or select contribution…"
                        readOnly={!isRecordingEditable}
                        disabled={!isRecordingEditable}
                        className={fieldControlClass()}
                      />
                      {addContributionDropdownOpen && (
                        <div
                          className={`${DROPDOWN_PANEL_CLASS} tw-overflow-x-hidden`}
                        >
                          {(() => {
                            const q = contributionSearchQuery
                              .trim()
                              .toLowerCase();
                            const filtered = availableContributions.filter(
                              (cont) =>
                                (!q || cont.toLowerCase().includes(q)) &&
                                !contributions.some(
                                  (c) => c.contribution === cont
                                )
                            );
                            if (filtered.length === 0) {
                              return (
                                <div className={DROPDOWN_EMPTY_CLASS}>
                                  No matching contributions
                                </div>
                              );
                            }
                            return filtered.map((cont) => (
                              <button
                                key={cont}
                                type="button"
                                className={DROPDOWN_OPTION_CLASS}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setContributionModalForm({
                                    contributionName: cont,
                                    confidenceCategory: '1',
                                    evidenceUrl: '',
                                    notes: '',
                                    personaId: resolveAliasIdByStageName(
                                      row?.stageName,
                                      clientAliases
                                    ),
                                  });
                                  setContributionModalContext({
                                    type: 'add',
                                    contributionValue: cont,
                                  });
                                  fetchClientAliases();
                                  setShowContributionModal(true);
                                  setAddContributionDropdownOpen(false);
                                  setContributionSearchQuery('');
                                }}
                              >
                                {capitalize(cont)}
                              </button>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  </div>

                  {mode === 'create' && versionAndLiveFields}

                  {/* ISRCs (Multiple) */}
                  <div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label
                        htmlFor="modal-isrcs-input"
                        className={`tw-block tw-text-sm tw-font-medium ${
                          isFieldModified('isrcs')
                            ? 'tw-text-white'
                            : 'tw-text-white'
                        }`}
                      >
                        ISRCs
                      </label>
                      <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                    </div>
                    {isrcs.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-min-h-[40px]">
                        {isrcs.map((isrc, index) => (
                          <ValueChip
                            key={index}
                            value={isrc}
                            edited={addedIsrcValues.has(isrc)}
                            changed={!originalValues?.isrcs?.includes(isrc)}
                            dimmed={!isRecordingEditable}
                            onRemove={
                              isRecordingEditable
                                ? () => {
                                    removeMultipleValue(setIsrcs, index);
                                    if (isrcFormatError)
                                      setIsrcFormatError(null);
                                  }
                                : undefined
                            }
                          />
                        ))}
                      </div>
                    )}
                    {isrcs.length === 0 && (
                      <p className="tw-text-textSeal tw-text-xs tw-mb-2">
                        None
                      </p>
                    )}
                    {isRecordingEditable && (
                      <div className="tw-flex tw-flex-col tw-gap-1.5">
                        {isrcInputOpen ? (
                          <>
                            <input
                              id="modal-isrcs-input"
                              type="text"
                              autoFocus
                              placeholder="Enter ISRC"
                              value={isrcInputValue}
                              onChange={(e) => {
                                setIsrcInputValue(e.target.value);
                                if (isrcFormatError) setIsrcFormatError(null);
                              }}
                              onKeyDown={(e) => {
                                if (
                                  e.key === 'Enter' &&
                                  isrcInputValue.trim()
                                ) {
                                  e.preventDefault();
                                  addIsrc(isrcInputValue.trim());
                                } else if (e.key === 'Escape') {
                                  setIsrcInputValue('');
                                  setIsrcInputOpen(false);
                                  setIsrcFormatError(null);
                                }
                              }}
                              className={fieldControlClass({
                                className: 'tw-flex-1',
                              })}
                            />
                            <div className="tw-flex tw-justify-end tw-gap-2">
                              <WButton
                                variant="secondary"
                                label="Cancel"
                                onClick={() => {
                                  setIsrcInputValue('');
                                  setIsrcInputOpen(false);
                                  setIsrcFormatError(null);
                                }}
                              />
                              <WButton
                                variant="primary"
                                label="Create"
                                disabled={!isrcInputValue.trim()}
                                onClick={() => addIsrc(isrcInputValue.trim())}
                              />
                            </div>
                          </>
                        ) : (
                          <div className="tw-flex tw-justify-end">
                            <button
                              type="button"
                              className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                              onClick={() => {
                                setIsrcInputValue('');
                                setIsrcFormatError(null);
                                setIsrcInputOpen(true);
                              }}
                            >
                              <i className="wi wi-20px wi-plus tw-mr-1" />
                              <span className="tw-underline">Add ISRC</span>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {isrcFormatError && (
                      <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
                        {isrcFormatError}
                      </p>
                    )}
                  </div>

                  {/* Duration, Featured — stacked single column */}
                  <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                    <div className="tw-flex-1 tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <label
                          htmlFor="modal-duration"
                          className={`tw-block tw-text-sm tw-font-medium ${
                            isFieldModified('duration')
                              ? 'tw-text-white'
                              : originalValues?.formData?.duration &&
                                !isFieldEdited('duration')
                              ? 'tw-text-textSeal'
                              : 'tw-text-white'
                          }`}
                        >
                          Duration
                        </label>
                        {mode === 'create' && (
                          <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                        )}
                      </div>
                      <ManualChangeWrap
                        edited={isFieldEdited('duration')}
                        changed={isFieldModified('duration')}
                      >
                        <input
                          id="modal-duration"
                          type="text"
                          value={formData.duration || ''}
                          onChange={(e) =>
                            handleFieldChange('duration', e.target.value)
                          }
                          disabled={
                            !isRecordingEditable ||
                            (!!originalValues?.formData?.duration
                              ?.toString()
                              .trim() &&
                              !isFieldEdited('duration'))
                          }
                          className={fieldControlClass({
                            error:
                              (!originalValues?.formData?.duration
                                ?.toString()
                                .trim() ||
                                isFieldEdited('duration')) &&
                              !!formData.duration &&
                              !isValidDuration(formData.duration),
                          })}
                          placeholder="HH:MM:SS"
                        />
                      </ManualChangeWrap>
                      {(!originalValues?.formData?.duration
                        ?.toString()
                        .trim() ||
                        isFieldEdited('duration')) &&
                        formData.duration &&
                        !isValidDuration(formData.duration) && (
                          <p className={`${FIELD_ERROR_CLASS} tw-mt-0.5`}>
                            Format: HH:MM:SS (e.g. 00:03:45)
                          </p>
                        )}
                    </div>

                    {/* Featured is derived from the row's artists, so at
                        create — where there is no row — it has nothing to
                        show and is left out rather than rendered disabled. */}
                    {mode === 'edit' && (
                      <div className="tw-flex-1 tw-min-w-0">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                          <label
                            htmlFor="modal-featured"
                            className="tw-block tw-text-sm tw-font-medium tw-text-textSeal"
                          >
                            Featured
                          </label>
                        </div>
                        <select
                          id="modal-featured"
                          value={formData.isFeatured ? 'F' : 'NF'}
                          disabled
                          className={`${SELECT_BASE_CLASS} ${selectTextClass(
                            true
                          )}`}
                        >
                          <option value="NF">Non-Featured (NF)</option>
                          <option value="F">Featured (F)</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Year of Recording / Country of Recording / (P) Name */}
                  <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                    <div className="tw-flex-1 tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <label
                          htmlFor="modal-year-recorded"
                          className="tw-block tw-text-sm tw-font-medium tw-text-white"
                        >
                          Year of Recording
                        </label>
                        <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                      </div>
                      <ManualChangeWrap
                        edited={isFieldEdited('yearRecorded')}
                        changed={isFieldModified('yearRecorded')}
                      >
                        <input
                          id="modal-year-recorded"
                          type="text"
                          value={formData.yearRecorded ?? ''}
                          onChange={(e) => {
                            const yearValue = e.target.value
                              .replace(/\D/g, '')
                              .slice(0, 4);
                            setYearRecordedError(
                              validateRecordingYear(yearValue)
                            );
                            setRuleErrorSide('recording');
                            handleFieldChange('yearRecorded', yearValue);
                          }}
                          disabled={!isRecordingEditable}
                          className={fieldControlClass({
                            error:
                              !!yearRecordedError ||
                              (ruleErrorSide === 'recording' &&
                                !!recordingBeforeReleaseError),
                          })}
                          placeholder="YYYY"
                          maxLength={4}
                        />
                      </ManualChangeWrap>
                      {yearRecordedError && (
                        <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
                          {yearRecordedError}
                        </p>
                      )}
                      {!yearRecordedError &&
                        ruleErrorSide === 'recording' &&
                        recordingBeforeReleaseError && (
                          <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
                            {recordingBeforeReleaseError}
                          </p>
                        )}
                    </div>

                    <div className="tw-flex-1 tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <label
                          htmlFor="modal-country-of-recording"
                          className="tw-block tw-text-sm tw-font-medium tw-text-white"
                        >
                          Country of Recording
                        </label>
                        <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                      </div>
                      <ManualChangeWrap
                        edited={isFieldEdited('countryOfRecording')}
                        changed={isFieldModified('location')}
                      >
                        <select
                          id="modal-country-of-recording"
                          value={formData.location ?? ''}
                          onChange={(e) =>
                            handleFieldChange('location', e.target.value)
                          }
                          disabled={!isRecordingEditable}
                          className={fieldControlClass({
                            kind: 'select',
                            hasValue: !!formData.location,
                          })}
                        >
                          <option value="">Select country</option>
                          {countryCodes.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                      </ManualChangeWrap>
                    </div>

                    <div className="tw-flex-1 tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <label
                          htmlFor="modal-pnames-input"
                          className="tw-block tw-text-sm tw-font-medium tw-text-white"
                        >
                          (P) Name
                        </label>
                        <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                      </div>
                      {pNames.length > 0 && (
                        <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-min-h-[40px]">
                          {pNames.map((pName, index) => (
                            <ValueChip
                              key={index}
                              value={pName}
                              edited={addedPNameValues.has(pName)}
                              changed={!originalValues?.pNames?.includes(pName)}
                              dimmed={!isRecordingEditable}
                              onRemove={
                                isRecordingEditable
                                  ? () => removeMultipleValue(setPNames, index)
                                  : undefined
                              }
                            />
                          ))}
                        </div>
                      )}
                      {pNames.length === 0 && (
                        <p className="tw-text-textSeal tw-text-xs tw-mb-2">
                          None
                        </p>
                      )}
                      {isRecordingEditable && (
                        <div className="tw-flex tw-flex-col tw-gap-1.5">
                          {pNameInputOpen ? (
                            <>
                              <input
                                id="modal-pnames-input"
                                type="text"
                                autoFocus
                                placeholder="Enter (P) name"
                                value={pNameInputValue}
                                onChange={(e) =>
                                  setPNameInputValue(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (
                                    e.key === 'Enter' &&
                                    pNameInputValue.trim()
                                  ) {
                                    e.preventDefault();
                                    addMultipleValue(
                                      setPNames,
                                      pNameInputValue
                                    );
                                    setPNameInputValue('');
                                    setPNameInputOpen(false);
                                  } else if (e.key === 'Escape') {
                                    setPNameInputValue('');
                                    setPNameInputOpen(false);
                                  }
                                }}
                                className={fieldControlClass({
                                  className: 'tw-flex-1',
                                })}
                              />
                              <div className="tw-flex tw-justify-end tw-gap-2">
                                <WButton
                                  variant="secondary"
                                  label="Cancel"
                                  onClick={() => {
                                    setPNameInputValue('');
                                    setPNameInputOpen(false);
                                  }}
                                />
                                <WButton
                                  variant="primary"
                                  label="Create"
                                  disabled={!pNameInputValue.trim()}
                                  onClick={() => {
                                    addMultipleValue(
                                      setPNames,
                                      pNameInputValue
                                    );
                                    setPNameInputValue('');
                                    setPNameInputOpen(false);
                                  }}
                                />
                              </div>
                            </>
                          ) : (
                            <div className="tw-flex tw-justify-end">
                              <button
                                type="button"
                                className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                                onClick={() => {
                                  setPNameInputValue('');
                                  setPNameInputOpen(true);
                                }}
                              >
                                <i className="wi wi-20px wi-plus tw-mr-1" />
                                <span className="tw-underline">
                                  Add (P) name
                                </span>
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section-level Cancel / Save. Edit mode only: it saves the
                      recording section on its own, and in create mode there is
                      no recording to save a section of — the whole form is one
                      create, committed from the footer below. */}
                  {mode === 'edit' && (
                    <>
                      {/* Cancel / Save — always shown (no separate "edit" step),
                        disabled until the recording has unsaved changes. */}
                      <div className="tw-mt-3 tw-pt-4 tw-border-t tw-border-formInputBackground tw-pb-3">
                        {recordingSaveError && (
                          <p className="tw-text-westburyRed tw-text-xs tw-mb-3">
                            {recordingSaveError}
                          </p>
                        )}
                        <div className="tw-flex tw-gap-2 tw-items-center">
                          <WButton
                            variant="secondary"
                            label="Cancel"
                            onClick={handleRecordingCancel}
                            disabled={!isRecordingSectionModified()}
                          />
                          <WButton
                            variant="primary"
                            label={
                              recordingSaveLoading ? 'Saving…' : 'Save Changes'
                            }
                            onClick={handleSaveRecordingDetails}
                            disabled={
                              !isRecordingSectionModified() ||
                              recordingSaveLoading ||
                              !!yearRecordedError ||
                              !!recordingBeforeReleaseError
                            }
                          />
                          {/* KNOWN_ERROR_TEST demo — sprint-review only, remove after the review. */}
                          {knownErrorTestEnabled && (
                            <WButton
                              variant="secondary"
                              label={`Simulate error (${
                                KNOWN_ERROR_TEST_SCENARIOS[
                                  simulateErrorIndex %
                                    KNOWN_ERROR_TEST_SCENARIOS.length
                                ].label
                              })`}
                              onClick={async () => {
                                const scenario =
                                  KNOWN_ERROR_TEST_SCENARIOS[
                                    simulateErrorIndex %
                                      KNOWN_ERROR_TEST_SCENARIOS.length
                                  ];
                                setRecordingSaveError(
                                  await friendlySaveError(
                                    scenario.makeError(),
                                    'recording'
                                  )
                                );
                                setSimulateErrorIndex((i) => i + 1);
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Separator */}
            <div
              className={
                stacked
                  ? 'tw-border-t tw-border-formInputBackground tw-shrink-0'
                  : 'tw-border-l tw-border-formInputBackground'
              }
            />
          </>
        )}

        {/* Release Section */}
        <div
          className={
            stacked
              ? 'tw-p-3 tw-flex tw-flex-col tw-shrink-0'
              : 'tw-pr-4 tw-p-3 tw-flex tw-flex-col tw-overflow-y-auto'
          }
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-pb-1.5 tw-border-b tw-border-formInputBackground tw-flex-shrink-0">
            <div className="tw-text-white tw-font-semibold">Release</div>
            {mode === 'create' && (
              <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
            )}
          </div>
          <div className="tw-space-y-4 tw-flex-1">
            {/* The release fields. Create mode shows them only once a release
                has been chosen — before that there is nothing to display and
                a column of empty disabled inputs reads as a broken form
                rather than as a step not yet taken. */}
            {(mode === 'edit' || !!releaseSelection) && (
              <>
                {/* Release Title and Year of Release - stacked single column */}
                <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                      <label
                        htmlFor="modal-release-title"
                        className={`tw-block tw-text-sm tw-font-medium ${
                          isFieldModified('release')
                            ? 'tw-text-white'
                            : 'tw-text-white'
                        }`}
                      >
                        Release Title
                      </label>
                    </div>
                    <ManualChangeWrap
                      edited={isFieldEdited('releaseTitle')}
                      changed={isFieldModified('release')}
                    >
                      <input
                        id="modal-release-title"
                        type="text"
                        value={formData.release || ''}
                        onChange={(e) =>
                          handleFieldChange('release', e.target.value)
                        }
                        readOnly={!isReleaseEditable}
                        disabled={!isReleaseEditable}
                        className={fieldControlClass()}
                      />
                    </ManualChangeWrap>
                  </div>
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label
                        htmlFor="modal-year"
                        className={`tw-block tw-text-sm tw-font-medium ${
                          isFieldModified('year')
                            ? 'tw-text-white'
                            : 'tw-text-white'
                        }`}
                      >
                        Year of Release
                      </label>
                      <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                    </div>
                    <ManualChangeWrap
                      edited={isFieldEdited('dateReleased')}
                      changed={isFieldModified('year')}
                    >
                      <input
                        id="modal-year"
                        type="text"
                        value={formData.year ?? ''}
                        onChange={(e) => {
                          const yearValue = e.target.value
                            .replace(/\D/g, '')
                            .slice(0, 4);
                          setYearError(validateReleaseYear(yearValue));
                          setRuleErrorSide('release');
                          handleFieldChange('year', yearValue);
                        }}
                        readOnly={!isReleaseEditable}
                        disabled={!isReleaseEditable}
                        className={fieldControlClass({ error: !!yearError })}
                        placeholder="YYYY"
                        maxLength={4}
                      />
                    </ManualChangeWrap>
                    {yearError && (
                      <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
                        {yearError}
                      </p>
                    )}
                    {!yearError &&
                      ruleErrorSide === 'release' &&
                      recordingBeforeReleaseError && (
                        <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
                          {recordingBeforeReleaseError}
                        </p>
                      )}
                  </div>
                </div>

                {/* Label (Multiple) */}
                <div>
                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                    <label
                      htmlFor="modal-label-input"
                      className={`tw-block tw-text-sm tw-font-medium ${
                        isFieldModified('label')
                          ? 'tw-text-white'
                          : 'tw-text-white'
                      }`}
                    >
                      Label (Multiple)
                    </label>
                    <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                  </div>
                  {labels.length > 0 && (
                    <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-min-h-[40px]">
                      {labels.map((label, index) => {
                        const isLabelAdded =
                          !originalValues?.labels?.includes(label);
                        return (
                          <ManualChangeChipWrap
                            key={index}
                            edited={addedLabelValues.has(label)}
                          >
                            <div
                              className={`tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-formInputBackground tw-text-white ${
                                !isReleaseEditable ? 'tw-opacity-60' : ''
                              }`}
                            >
                              {isLabelAdded && (
                                <span className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-surfacePurple tw-border tw-border-gray-900 tw-animate-pulse-slow" />
                              )}
                              <span>{label}</span>
                              {isReleaseEditable && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeMultipleValue(setLabels, index)
                                  }
                                  className="tw-ml-1 tw-text-textSeal hover:tw-text-white tw-flex tw-items-center"
                                  title="Remove"
                                >
                                  <X size={12} />
                                </button>
                              )}
                            </div>
                          </ManualChangeChipWrap>
                        );
                      })}
                    </div>
                  )}
                  {labels.length === 0 && (
                    <p className="tw-text-textSeal tw-text-xs tw-mb-2">None</p>
                  )}
                  {isReleaseEditable && (
                    <div className="tw-flex tw-flex-col tw-gap-1.5">
                      {labelInputOpen ? (
                        <>
                          <input
                            id="modal-label-input"
                            type="text"
                            autoFocus
                            placeholder="Enter label"
                            value={labelInputValue}
                            onChange={(e) => setLabelInputValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && labelInputValue.trim()) {
                                e.preventDefault();
                                commitLabel();
                              } else if (e.key === 'Escape') {
                                setLabelInputValue('');
                                setLabelInputOpen(false);
                              }
                            }}
                            className={fieldControlClass({
                              className: 'tw-flex-1',
                            })}
                          />
                          <div className="tw-flex tw-justify-end tw-gap-2">
                            <WButton
                              variant="secondary"
                              label="Cancel"
                              onClick={() => {
                                setLabelInputValue('');
                                setLabelInputOpen(false);
                              }}
                            />
                            <WButton
                              variant="primary"
                              label="Create"
                              disabled={!labelInputValue.trim()}
                              onClick={commitLabel}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="tw-flex tw-justify-end">
                          <button
                            type="button"
                            className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                            onClick={() => {
                              setLabelInputValue('');
                              setLabelInputOpen(true);
                            }}
                          >
                            <i className="wi wi-20px wi-plus tw-mr-1" />
                            <span className="tw-underline">Add label</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {labelsError && (
                    <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
                      {labelsError}
                    </p>
                  )}
                </div>

                {/* Cat no. (Multiple) */}
                <div>
                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                    <label
                      htmlFor="modal-catno-input"
                      className={`tw-block tw-text-sm tw-font-medium ${
                        isFieldModified('catNumbers')
                          ? 'tw-text-white'
                          : 'tw-text-white'
                      }`}
                    >
                      Catalogue Number (Multiple)
                    </label>
                    <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                  </div>
                  {catNumbers.length > 0 && (
                    <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-min-h-[40px]">
                      {catNumbers.map((catNo, index) => {
                        const isCatNoAdded =
                          !originalValues?.catNumbers?.includes(catNo);
                        return (
                          <ManualChangeChipWrap
                            key={index}
                            edited={addedCatNumberValues.has(catNo)}
                          >
                            <div
                              className={`tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-formInputBackground tw-text-white ${
                                !isReleaseEditable ? 'tw-opacity-60' : ''
                              }`}
                            >
                              {isCatNoAdded && (
                                <span className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-surfacePurple tw-border tw-border-gray-900 tw-animate-pulse-slow" />
                              )}
                              <span>{catNo}</span>
                              {isReleaseEditable && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeMultipleValue(setCatNumbers, index)
                                  }
                                  className="tw-ml-1 tw-text-textSeal hover:tw-text-white tw-flex tw-items-center"
                                  title="Remove"
                                >
                                  <X size={12} />
                                </button>
                              )}
                            </div>
                          </ManualChangeChipWrap>
                        );
                      })}
                    </div>
                  )}
                  {catNumbers.length === 0 && (
                    <p className="tw-text-textSeal tw-text-xs tw-mb-2">None</p>
                  )}
                  {isReleaseEditable && (
                    <div className="tw-flex tw-flex-col tw-gap-1.5">
                      {catNoInputOpen ? (
                        <>
                          <input
                            id="modal-catno-input"
                            type="text"
                            autoFocus
                            placeholder="Enter catalogue number"
                            value={catNoInputValue}
                            onChange={(e) => setCatNoInputValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && catNoInputValue.trim()) {
                                e.preventDefault();
                                commitCatNo();
                              } else if (e.key === 'Escape') {
                                setCatNoInputValue('');
                                setCatNoInputOpen(false);
                              }
                            }}
                            className={fieldControlClass({
                              className: 'tw-flex-1',
                            })}
                          />
                          <div className="tw-flex tw-justify-end tw-gap-2">
                            <WButton
                              variant="secondary"
                              label="Cancel"
                              onClick={() => {
                                setCatNoInputValue('');
                                setCatNoInputOpen(false);
                              }}
                            />
                            <WButton
                              variant="primary"
                              label="Create"
                              disabled={!catNoInputValue.trim()}
                              onClick={commitCatNo}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="tw-flex tw-justify-end">
                          <button
                            type="button"
                            className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                            onClick={() => {
                              setCatNoInputValue('');
                              setCatNoInputOpen(true);
                            }}
                          >
                            <i className="wi wi-20px wi-plus tw-mr-1" />
                            <span className="tw-underline">
                              Add catalogue number
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {catNumbersError && (
                    <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
                      {catNumbersError}
                    </p>
                  )}
                </div>

                {/* Format and Type - stacked single column */}
                <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label
                        htmlFor="modal-format"
                        className={`tw-block tw-text-sm tw-font-medium ${
                          isFieldModified('format')
                            ? 'tw-text-white'
                            : 'tw-text-white'
                        }`}
                      >
                        Format
                      </label>
                      <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                    </div>
                    <ManualChangeWrap
                      edited={isFieldEdited('format')}
                      changed={isFieldModified('format')}
                    >
                      <select
                        id="modal-format"
                        value={formData.format || ''}
                        onChange={(e) =>
                          handleFieldChange('format', e.target.value)
                        }
                        disabled={!isReleaseEditable}
                        className={`${SELECT_BASE_CLASS} ${selectTextClass(
                          !!formData.format
                        )}`}
                      >
                        <option value="">Select Format</option>
                        {availableFormats.map((format) => (
                          <option key={format} value={format}>
                            {format}
                          </option>
                        ))}
                      </select>
                    </ManualChangeWrap>
                  </div>
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label
                        htmlFor="modal-type"
                        className={`tw-block tw-text-sm tw-font-medium ${
                          isFieldModified('mediaType')
                            ? 'tw-text-white'
                            : 'tw-text-white'
                        }`}
                      >
                        Type
                      </label>
                      <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                    </div>
                    <ManualChangeWrap
                      edited={isFieldEdited('mediaType')}
                      changed={isFieldModified('mediaType')}
                    >
                      <select
                        id="modal-type"
                        value={formData.mediaType || ''}
                        onChange={(e) =>
                          handleFieldChange('mediaType', e.target.value)
                        }
                        disabled={!isReleaseEditable}
                        className={`${SELECT_BASE_CLASS} ${selectTextClass(
                          !!formData.mediaType
                        )}`}
                      >
                        <option value="">Select Type</option>
                        {availableTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </ManualChangeWrap>
                  </div>
                </div>

                {/* Barcode */}
                <div>
                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                    <label
                      htmlFor="modal-barcode"
                      className="tw-block tw-text-sm tw-font-medium tw-text-white"
                    >
                      Barcode
                    </label>
                    <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
                  </div>
                  <ManualChangeWrap
                    edited={isFieldEdited('barcode')}
                    changed={isFieldModified('barcode')}
                  >
                    <input
                      id="modal-barcode"
                      type="text"
                      value={formData.barcode ?? ''}
                      onChange={(e) =>
                        handleFieldChange('barcode', e.target.value)
                      }
                      readOnly={!isReleaseEditable}
                      disabled={!isReleaseEditable}
                      className={fieldControlClass({ error: !!barcodeError })}
                      placeholder="UPC or EAN"
                    />
                  </ManualChangeWrap>
                  {barcodeError && (
                    <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
                      {barcodeError}
                    </p>
                  )}
                </div>
              </>
            )}
            {mode === 'create' && !releaseSelection && (
              <div className="tw-rounded-lg tw-border tw-border-dashed tw-border-formInputBackground tw-p-6 tw-text-center">
                <p className="tw-text-white tw-text-sm">No release linked yet</p>
                {/* The one piece of guidance the empty state carries, and only
                    while the button below is dead: a disabled Add Release with
                    no reason given is a broken button, not a step not yet
                    taken. */}
                {releaseWaitsForTrackPick && (
                  <p className="tw-text-gray-400 tw-text-xs tw-mt-2">
                    Pick the track first — the release is the Discogs release
                    it comes from.
                  </p>
                )}
              </div>
            )}

            {releasePinError && (
              <p className={`${FIELD_ERROR_CLASS} tw-mt-3`}>{releasePinError}</p>
            )}

            {releaseSaveError && (
              <p className="tw-text-westburyRed tw-text-xs tw-mt-3">
                {releaseSaveError}
              </p>
            )}
            {/* Release Actions */}
            <div className="tw-flex tw-gap-2 tw-items-center tw-mt-3 tw-pt-4 tw-border-t tw-border-formInputBackground">
              {/* Create mode gets its own pair: the edit-mode buttons below
                  address a release already linked to a recording, and
                  ChangeReleaseModal's apply path is gated on `row?.id`. Create
                  uses the same modal in 'select' mode, which returns the choice
                  instead of performing the link. */}
              {mode === 'create' && (
                <>
                  <WButton
                    variant="secondary"
                    label={releaseSelection ? 'Change Release' : 'Add Release'}
                    disabled={releaseWaitsForTrackPick}
                    onClick={() => {
                      if (releaseWaitsForTrackPick) return;
                      setShowChangeReleaseModal(true);
                    }}
                  />
                  {/* Clearing is not the same as never having chosen: it has to
                      put the fields *and* the linkage back, or the checklist
                      would go on reporting the release as satisfied. */}
                  {releaseSelection && (
                    <WButton
                      variant="secondary"
                      label="Clear"
                      onClick={clearReleaseSelection}
                    />
                  )}
                </>
              )}
              {mode === 'edit' && !isReleaseEditable && (
                <>
                  <WButton
                    variant="secondary"
                    label="Edit Release Details"
                    onClick={() => setShowReleaseCorrectionModal(true)}
                  />
                  <WButton
                    variant="secondary"
                    label="Change Linked Release"
                    onClick={() => setShowChangeReleaseModal(true)}
                  />
                </>
              )}
              {isReleaseEditable && (
                <>
                  <WButton
                    variant="secondary"
                    label="Cancel"
                    onClick={handleReleaseCancel}
                  />
                  <WButton
                    variant="primary"
                    label={
                      releaseSaveLoading ? 'Saving…' : 'Save Release Details'
                    }
                    onClick={handleSaveReleaseDetails}
                    disabled={
                      releaseSaveLoading ||
                      !formData.release?.trim() ||
                      !!yearError ||
                      !!barcodeError ||
                      !!recordingBeforeReleaseError
                    }
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create footer — create mode only. Edit mode commits per section from
          the section CTAs above; a create is one commit, so it needs one
          footer. What is still missing is not spelled out beside the button:
          the form itself says so (a mandatory field is one without "Optional"
          against it), and the button's hover popover names the rest. */}
      {mode === 'create' && (
        // `shrink-0`: the scrolling columns above give way, never the footer.
        <div className="tw-border-t tw-border-formInputBackground tw-pt-4 tw-mt-2 tw-shrink-0">
          {/* Server-side rejections, above the button that produced them. Never
            a modal: these are fixable, and a modal would have to be dismissed
            before the operator could reach the fields. The strings are shown
            exactly as the backend worded them — nothing here interprets
            them. */}
          {createErrors.length > 0 && (
            <div className="tw-mb-3">
              <p className="tw-text-westburyRed tw-text-xs tw-mb-1">
                This recording was not created:
              </p>
              <ul className="tw-text-westburyRed tw-text-xs tw-list-disc tw-list-inside">
                {createErrors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </div>
          )}
          {createSubmitError && (
            <p className="tw-text-westburyRed tw-text-xs tw-mb-3">
              {createSubmitError}
            </p>
          )}
          <div className="tw-flex tw-gap-2 tw-items-center tw-justify-end">
            <WButton variant="secondary" label="Cancel" onClick={onCancel} />
            {/* `tw-group` on the wrapper rather than the button: `WButton`
                takes no extra handlers, and a disabled one is a plain `<div>`
                that still receives hover, which is exactly when the popover is
                wanted. Always in the DOM (hidden by CSS) so it is one element
                to test against, not a hover-timing race. */}
            <div className="tw-relative tw-group">
              <WButton
                variant="primary"
                label={createSubmitting ? 'Creating…' : 'Create Recording'}
                onClick={handleCreateSubmit}
                disabled={!canSubmitCreate || createSubmitting || !userId}
              />
              {!canSubmitCreate && (
                <div
                  role="tooltip"
                  className="tw-hidden group-hover:tw-block tw-absolute tw-bottom-full tw-right-0 tw-mb-2 tw-w-64 tw-z-[60] tw-rounded-lg tw-border-2 tw-border-formInputBackground tw-bg-surfaceBlack80 tw-p-3 tw-shadow-lg"
                >
                  {unmetCreateRequirements.length > 0 && (
                    <>
                      <p className="tw-text-white tw-text-xs tw-mb-1">
                        Still needed to create this recording:
                      </p>
                      <ul className="tw-text-textSeal tw-text-xs tw-list-disc tw-list-inside">
                        {unmetCreateRequirements.map((requirement) => (
                          <li key={requirement.label}>{requirement.label}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {createFormatErrors && (
                    // Deliberately does not restate the errors: each is already
                    // rendered against its own field, which is where the fix
                    // is made.
                    <p
                      className={`tw-text-westburyRed tw-text-xs ${
                        unmetCreateRequirements.length > 0 ? 'tw-mt-2' : ''
                      }`}
                    >
                      Fix the highlighted fields to continue.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Release Correction Confirmation Modal */}
      <WModal
        isOpen={showReleaseCorrectionModal}
        onClose={() => setShowReleaseCorrectionModal(false)}
        title="Confirm Release Correction"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            You are about to correct release data that is linked to multiple
            recordings. Do you wish to proceed?
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => setShowReleaseCorrectionModal(false)}
            />
            <WButton
              variant="primary"
              label="Proceed"
              onClick={() => {
                setShowReleaseCorrectionModal(false);
                setIsReleaseEditable(true);
                // In a real implementation, this would trigger the release correction logic
                // For now, it's just a mock, so we enable editing
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Contribution detail modal (extracted, shared with bulk edit) */}
      <ContributionDetailModal
        isOpen={showContributionModal}
        mode={contributionModalContext?.type ?? 'add'}
        initialValue={contributionModalForm}
        aliases={clientAliases}
        aliasesLoading={clientAliasesLoading}
        preferredStageName={row?.stageName}
        onClose={() => {
          setShowContributionModal(false);
          setContributionModalContext(null);
        }}
        onSave={(payload) => {
          if (contributionModalContext?.type === 'edit') {
            const idx = contributionModalContext.index;
            setContributions((prev) =>
              prev.map((c, i) => (i === idx ? payload : c))
            );
          } else if (contributionModalContext?.type === 'add') {
            setContributions((prev) => [...prev, payload]);
          }
          setShowContributionModal(false);
          setContributionModalContext(null);
        }}
      />

      {/* Unsaved add-item warning modal (shown when saving with an inline
          ISRC / Label / Catalogue Number input still open) */}
      <WModal
        isOpen={unsavedItemWarning !== null}
        onClose={() => setUnsavedItemWarning(null)}
        title="Unsaved item"
        modalClasses="tw-max-w-sm tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            You have a{unsavedItemWarning === 'ISRC' ? 'n' : ''}{' '}
            <strong>{unsavedItemWarning}</strong> being entered. Please click{' '}
            <strong>Create</strong> to add it, or <strong>Cancel</strong>,
            before saving your changes.
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="primary"
              label="Got it"
              onClick={() => setUnsavedItemWarning(null)}
            />
          </div>
        </div>
      </WModal>

      {/* Remove contribution confirmation modal */}
      <WModal
        isOpen={showRemoveContributionModal !== null}
        onClose={() => setShowRemoveContributionModal(null)}
        title="Remove Contribution"
        modalClasses="tw-max-w-sm tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            Are you sure you want to remove{' '}
            <strong>
              {showRemoveContributionModal !== null
                ? capitalize(
                    contributions[showRemoveContributionModal]?.contribution ??
                      ''
                  )
                : ''}
            </strong>{' '}
            from this recording?
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => setShowRemoveContributionModal(null)}
            />
            <WButton
              variant="primary"
              label="Remove"
              onClick={() => {
                if (showRemoveContributionModal !== null) {
                  const removeIdx = showRemoveContributionModal;
                  setContributions((prev) =>
                    prev.filter((_, i) => i !== removeIdx)
                  );
                  if (
                    contributionModalContext?.type === 'edit' &&
                    contributionModalContext.index === removeIdx
                  ) {
                    setShowContributionModal(false);
                    setContributionModalContext(null);
                  } else if (
                    contributionModalContext?.type === 'edit' &&
                    contributionModalContext.index > removeIdx
                  ) {
                    setContributionModalContext({
                      type: 'edit',
                      index: contributionModalContext.index - 1,
                    });
                  }
                  setShowRemoveContributionModal(null);
                }
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Artist demote/remove modal */}
      <WModal
        isOpen={artistActionModalIndex !== null}
        onClose={() => {
          setArtistActionModalIndex(null);
          setArtistActionConfirm(null);
        }}
        title={
          artistActionConfirm === 'demote'
            ? 'Confirm Demote'
            : artistActionConfirm === 'remove'
            ? 'Confirm Remove'
            : 'Artist Action'
        }
        modalClasses="tw-max-w-lg tw-w-full tw-mx-4"
      >
        {artistActionModalIndex !== null &&
          artists[artistActionModalIndex] &&
          (() => {
            const artist = artists[artistActionModalIndex];
            const idx = artistActionModalIndex;

            if (artistActionConfirm === 'demote') {
              return (
                <div className="tw-mt-4 tw-space-y-4">
                  <p className="tw-text-white tw-text-sm">
                    Are you sure you want to demote{' '}
                    <strong>{artist.name}</strong> to Non-Featured?
                  </p>
                  <div className="tw-flex tw-justify-end tw-gap-2">
                    <WButton
                      variant="secondary"
                      label="Cancel"
                      onClick={() => setArtistActionConfirm(null)}
                    />
                    <WButton
                      variant="primary"
                      label="Demote"
                      onClick={() => {
                        setDemotedArtists((prev) => [...prev, artist]);
                        setArtists((prev) => prev.filter((_, i) => i !== idx));
                        setArtistActionModalIndex(null);
                        setArtistActionConfirm(null);
                      }}
                    />
                  </div>
                </div>
              );
            }

            if (artistActionConfirm === 'remove') {
              return (
                <div className="tw-mt-4 tw-space-y-4">
                  <p className="tw-text-white tw-text-sm">
                    Are you sure you want to remove{' '}
                    <strong>{artist.name}</strong> from this recording?
                  </p>
                  <div className="tw-flex tw-justify-end tw-gap-2">
                    <WButton
                      variant="secondary"
                      label="Cancel"
                      onClick={() => setArtistActionConfirm(null)}
                    />
                    <WButton
                      variant="primary"
                      label="Remove"
                      onClick={() => {
                        setArtists((prev) => prev.filter((_, i) => i !== idx));
                        setArtistActionModalIndex(null);
                        setArtistActionConfirm(null);
                      }}
                    />
                  </div>
                </div>
              );
            }

            return (
              <div className="tw-mt-4 tw-space-y-3">
                <p className="tw-text-white tw-text-sm">
                  What would you like to do with <strong>{artist.name}</strong>?
                </p>
                <div className="tw-flex tw-flex-row tw-gap-2 tw-justify-end tw-pt-2">
                  <WButton
                    variant="secondary"
                    label="Demote to Non-Featured"
                    extraClasses="tw-flex-1 !tw-whitespace-normal !tw-h-auto tw-min-h-[32px] tw-py-1.5 tw-text-center"
                    onClick={() => setArtistActionConfirm('demote')}
                  />
                  <WButton
                    variant="secondary"
                    label="Remove from Recording"
                    extraClasses="tw-flex-1 !tw-whitespace-normal !tw-h-auto tw-min-h-[32px] tw-py-1.5 tw-text-center"
                    onClick={() => setArtistActionConfirm('remove')}
                  />
                </div>
              </div>
            );
          })()}
      </WModal>

      {/* Duplicate ISRC confirmation modal */}
      <WModal
        isOpen={pendingDuplicateIsrc !== null}
        onClose={() => {
          setPendingDuplicateIsrc(null);
          setPendingDuplicateMatches([]);
        }}
        title="Reassign ISRC"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-3">
            This ISRC is currently assigned to:
          </p>
          {pendingDuplicateMatches.length > 0 && (
            <div className="tw-mb-4 tw-space-y-1.5 tw-max-h-32 tw-overflow-y-auto">
              {pendingDuplicateMatches.map((m) => (
                <div
                  key={m.recordingId}
                  className="tw-flex tw-flex-col tw-bg-surfaceBlack90 tw-rounded tw-px-3 tw-py-2 tw-text-xs"
                >
                  <span className="tw-text-white tw-font-medium">
                    {m.recordingTitle || 'Untitled'}
                  </span>
                  {m.artistName && (
                    <span className="tw-text-textSeal">{m.artistName}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="tw-text-white tw-text-sm">
            Continuing will remove the ISRC from{' '}
            {pendingDuplicateMatches.length === 1
              ? 'that recording'
              : 'those recordings'}{' '}
            and assign it to{' '}
            <strong>{formData.title || 'this recording'}</strong>.
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-5">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setPendingDuplicateIsrc(null);
                setPendingDuplicateMatches([]);
              }}
            />
            <WButton
              variant="primary"
              label="Continue"
              onClick={() => {
                if (pendingDuplicateIsrc) {
                  addMultipleValue(setIsrcs, pendingDuplicateIsrc);
                  // Track all recordings this ISRC is being reassigned from
                  if (pendingDuplicateMatches.length > 0) {
                    setConfirmedDuplicateIsrcs((prev) => ({
                      ...prev,
                      [pendingDuplicateIsrc]: pendingDuplicateMatches.map(
                        (m) => ({
                          recordingId: m.recordingId,
                          documentId: m.documentId,
                        })
                      ),
                    }));
                  }
                  setIsrcInputValue('');
                  setIsrcInputOpen(false);
                  setPendingDuplicateIsrc(null);
                  setPendingDuplicateMatches([]);
                }
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Create new artist modal (extracted, shared with bulk edit) */}
      <ArtistEditorModal
        isOpen={showCreateArtist}
        onClose={() => setShowCreateArtist(false)}
        userId={userId}
        parentRecordingId={row?.id}
        allowNonFeatured={mode === 'create'}
        onAdd={(entry) => {
          setArtists((prev) => [...prev, entry]);
          setShowCreateArtist(false);
        }}
      />

      {/* Add existing artist modal (featured selection) */}
      <WModal
        isOpen={pendingArtistName !== null}
        onClose={() => {
          setPendingArtistName(null);
          setPendingArtistId(null);
          setPendingArtistFeatured('fa');
        }}
        title="Add artist"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4 tw-space-y-4">
          <div>
            <span className="tw-block tw-text-textSeal tw-text-xs tw-font-medium tw-mb-1">
              Artist
            </span>
            <p className="tw-text-white tw-text-sm">
              {pendingArtistName ?? ''}
            </p>
          </div>
          <div>
            <span className="tw-block tw-text-textSeal tw-text-xs tw-font-medium tw-mb-2">
              Featured Status
            </span>
            {/* Non-Featured is a create-only choice: the edit-add contract
                (4.1.3) takes `fa`/`ofa`, a non-featured artist on an existing
                recording being a demotion rather than an addition. */}
            {mode === 'create' ? (
              <SegmentedChoice
                options={ADD_ARTIST_STATUS_OPTIONS}
                value={pendingArtistFeatured}
                onChange={setPendingArtistFeatured}
              />
            ) : (
              <WmcSegmentedToggle
                leftLabel="Featured"
                rightLabel="Other Featured"
                isLeftSelected={pendingArtistFeatured === 'fa'}
                onSelectLeft={() => setPendingArtistFeatured('fa')}
                onSelectRight={() => setPendingArtistFeatured('ofa')}
              />
            )}
          </div>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setPendingArtistName(null);
                setPendingArtistId(null);
                setPendingArtistMembers([]);
                setPendingArtistFeatured('fa');
              }}
            />
            <WButton
              variant="primary"
              label="Add"
              onClick={() => {
                if (pendingArtistName) {
                  setArtists((prev) => [
                    ...prev,
                    {
                      name: pendingArtistName,
                      isFeatured: pendingArtistFeatured === 'fa',
                      featuredStatus: pendingArtistFeatured,
                      id: pendingArtistId ?? undefined,
                      members:
                        pendingArtistMembers.length > 0
                          ? pendingArtistMembers
                          : undefined,
                    },
                  ]);
                  setPendingArtistName(null);
                  setPendingArtistId(null);
                  setPendingArtistMembers([]);
                  setPendingArtistFeatured('fa');
                }
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Change/Link Release modal (extracted, shared with bulk edit) */}
      <ChangeReleaseModal
        isOpen={showChangeReleaseModal}
        onClose={() => setShowChangeReleaseModal(false)}
        // 'select' returns the chosen release instead of linking it — there is
        // no recording to link it to yet. The modal is otherwise the same one
        // edit mode and bulk edit use, including its lookup-before-create
        // guard, so create inherits the only duplicate-release protection
        // there is.
        mode={mode === 'create' ? 'select' : 'apply'}
        userId={userId}
        row={row}
        current={{
          release: formData.release,
          year: formData.year,
          format: formData.format,
          mediaType: formData.mediaType,
          barcode: formData.barcode,
        }}
        currentLabels={labels}
        currentCatNumbers={catNumbers}
        availableFormats={availableFormats}
        availableTypes={availableTypes}
        yearRecorded={formData.yearRecorded}
        // A release shares its recording's source (`create-release-source-locked`),
        // and a Discogs recording goes on the release it was picked from and
        // no other (`create-discogs-release-pinned`).
        pinnedRelease={
          mode === 'create' && createSource
            ? {
                source: releaseSourceFormOf(createSource),
                sourceId: pinnedDiscogsReleaseId ?? undefined,
              }
            : undefined
        }
        onApplied={(updatedRow, selection) => {
          setFormData((prev) => ({
            ...prev,
            release: updatedRow.release,
            year: updatedRow.year ?? '',
            format: updatedRow.format ?? '',
            mediaType: updatedRow.mediaType ?? '',
            barcode: updatedRow.barcode ?? '',
          }));
          setLabels(updatedRow.labelsArray);
          setCatNumbers(updatedRow.catNumbersArray);
          if (mode === 'create') {
            // `selection` is only supplied in 'select' mode. Nothing is saved
            // here — the create commits once, from the footer.
            setReleaseSelection(selection ?? null);
            setShowChangeReleaseModal(false);
            return;
          }
          onSave(updatedRow);
          setEditsVersion((v) => v + 1);
        }}
      />

      {/* Live note modal — sets/updates the live descriptor (string). */}
      <WModal
        isOpen={showLiveToYesModal}
        onClose={() => {
          setShowLiveToYesModal(false);
          setLiveToYesNote('');
        }}
        title={
          liveToString(formData.live)
            ? 'Update Live Information'
            : 'Mark Recording as Live'
        }
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-4">
            Provide Live information for this recording. This is stored on the
            Live field and can be updated later
            {mode === 'create'
              ? ', and removed until the recording is created.'
              : ', but not removed.'}
          </p>
          <label
            htmlFor="live-to-yes-note"
            className={`${FIELD_LABEL_CLASS} tw-mb-2`}
          >
            Live Information{' '}
            <span className={FIELD_ERROR_CLASS}>(required)</span>
          </label>
          <textarea
            id="live-to-yes-note"
            value={liveToYesNote}
            onChange={(e) => setLiveToYesNote(e.target.value)}
            placeholder="e.g. Live in London, 1989"
            rows={3}
            className={fieldControlClass({ kind: 'textarea' })}
          />
          <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowLiveToYesModal(false);
                setLiveToYesNote('');
              }}
            />
            <WButton
              variant="primary"
              label="Confirm"
              disabled={!liveToYesNote.trim()}
              onClick={() => {
                // Set the live descriptor on its own. It lives solely in the
                // `live` field — no recordingVersion op is involved.
                handleFieldChange('live', liveToYesNote.trim());
                setShowLiveToYesModal(false);
                setLiveToYesNote('');
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Revert Confirmation Modal */}
      <WModal
        isOpen={showRevertModal}
        onClose={() => {
          setShowRevertModal(false);
          setRevertTarget(null);
        }}
        title="Confirm Revert"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            Do you want to revert to the original value?
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowRevertModal(false);
                setRevertTarget(null);
              }}
            />
            <WButton
              variant="primary"
              label="Revert"
              onClick={() => {
                if (revertTarget) {
                  handleRevert(revertTarget.type, revertTarget.field);
                }
              }}
            />
          </div>
        </div>
      </WModal>
    </>
  );
});
